import { useState, useEffect, useRef, useCallback, forwardRef } from "react";
import { flushSync, createPortal } from "react-dom";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useReloadOnRouteEnter } from "../hooks/useReloadOnRouteEnter";
import { useTheme } from "../components/ThemeProvider";
import { useAuth } from "../context/AuthContext";
import usePermiso from "../hooks/usePermiso";
import { dialogs } from "../context/DialogContext";
import api from "../services/api";
import { textIncludesAny } from "../utils/searchText";
import FichaResumenPropiedad from "../components/propiedades/FichaResumenPropiedad";
import { resolverPropiedadIdPorReferencia } from "../utils/resolverPropiedadIdPorReferencia";
import { usePipelineCardClickDrag } from "../utils/pipelineCardPointer";
import {
  filtroFechaRangoMatch as filtroFechaPipelineMatch,
  abrirPickerFecha,
} from "../utils/filtroFechaRango";

const IMG_PROXY_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:4000/api/img-proxy?url='
  : 'https://vitalcasa-crm-production.up.railway.app/api/img-proxy?url=';
const proxyFoto = (url) => url ? IMG_PROXY_BASE + encodeURIComponent(url) : url;

/** Fecha en tarjetas pipeline: cita agendada (agenda) o, si no hay, alta del registro.
 * En Eliminadas/Fríos: updated_at = momento de archivo / vencimiento (columna «Fecha / Hora Vto»).
 * El alta real va en la columna «Fecha / Hora Alta» (created_at).
 */
function fechaReferenciaPipeline(item, opts = {}) {
  const tipo = opts.tipoModal || opts.contexto || null;
  if (tipo === "eliminados" || tipo === "frios") {
    return item?.updated_at || item?.fecha_cita || item?.created_at || null;
  }
  return item?.fecha_cita || item?.created_at || null;
}

function formatCeldaFechaHora(iso, c) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const fechaStr = d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" });
  const horaStr = d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  return (
    <span style={{ whiteSpace: "nowrap" }} title={d.toLocaleString("es-ES")}>
      {fechaStr}
      <span style={{ color: c.textMuted, marginLeft: 6 }}>{horaStr}</span>
    </span>
  );
}

function formatFechaHoraPipeline(iso) {
  if (!iso) return { fechaStr: "", horaStr: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { fechaStr: "", horaStr: "" };
  return {
    fechaStr: d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" }),
    horaStr: d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }),
  };
}

/** Líneas del tooltip: cada entrada a etapa en orden cronológico (reingresos incluidos). */
function lineasEtapasEntradaCaptacion(item) {
  const raw = item?.etapas_entrada;
  const metaByBack = {};
  for (const et of ETAPAS_CAPTACIONES) {
    const back = MAPA_ETAPAS_CAPTACIONES_FRONT_TO_BACK[et.key];
    if (back) metaByBack[back] = et;
  }

  const lineas = [];
  const pushLinea = (backKey, iso, idx) => {
    const et = metaByBack[backKey];
    if (!et || !iso) return;
    const { fechaStr, horaStr } = formatFechaHoraPipeline(iso);
    if (!fechaStr) return;
    lineas.push({
      key: `${backKey}-${idx}-${iso}`,
      label: et.label,
      color: et.color,
      texto: `${fechaStr}${horaStr ? ` · ${horaStr}` : ""}`,
    });
  };

  if (Array.isArray(raw) && raw.length) {
    raw.forEach((row, idx) => {
      const back = row?.etapa || row?.key;
      const iso = row?.at || row?.fecha || row?.iso;
      pushLinea(back, iso, idx);
    });
    return lineas;
  }

  // Compat: mapa antiguo { visita_cap: ISO, ... }
  const map = raw && typeof raw === "object" && !Array.isArray(raw)
    ? raw
    : (typeof item?.datos_extras === "object" && item.datos_extras?.etapas_entrada) || {};
  let idx = 0;
  for (const et of ETAPAS_CAPTACIONES) {
    const back = MAPA_ETAPAS_CAPTACIONES_FRONT_TO_BACK[et.key];
    const iso = map?.[back];
    if (!iso) continue;
    pushLinea(back, iso, idx++);
  }
  return lineas;
}

function isAbortError(err) {
  return err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError";
}

/** Misma forma que Agenda/Tareas: { data: [] } o array suelto. */
function parseListaUsuariosRespuesta(res) {
  const raw = res?.data;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.usuarios)) return raw.usuarios;
  if (Array.isArray(raw)) return raw;
  return [];
}

function normalizarUsuariosFiltroPipeline(lista) {
  return (Array.isArray(lista) ? lista : [])
    .filter((u) => u && u.activo !== false && (u.id != null || u.agente_id != null))
    .map((u) => ({
      ...u,
      id: String(u.id || u.agente_id),
      agente_id: u.agente_id != null && u.agente_id !== "" ? String(u.agente_id) : (u.agente_id || null),
      nombre: u.nombre || u.email || "Sin nombre",
      rol: u.rol || "",
      agente_es_ia: u.agente_es_ia === true || u.es_ia === true || String(u.rol || "").toLowerCase() === "ia",
    }))
    .sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""), "es"));
}

/** Usuario del filtro que representa a Ana (IA / quien_trabaja=ana en captaciones). */
function esUsuarioAnaEnFiltroPipeline(u) {
  if (!u) return false;
  if (u.agente_es_ia === true || u.es_ia === true) return true;
  if (String(u.rol || "").toLowerCase() === "ia") return true;
  const n = String(u.nombre || "").trim().toLowerCase();
  return n === "ana" || n.startsWith("ana ");
}

/** COUNT(*) (`total`) o, si el backend aún no lo envía, longitud del listado. */
function totalDesdeRespuestaLista(res) {
  if (typeof res?.data?.total === "number") return res.data.total;
  const arr = res?.data?.data;
  return Array.isArray(arr) ? arr.length : 0;
}

import ModalNuevoLead from "../components/ModalNuevoLead";
import ModalNuevaPrecaptacion from "../components/ModalNuevaPrecaptacion";
import ModalRevisarCaptacion from "../components/ModalRevisarCaptacion";
import ModalMotivoRetrocesoCaptacion from "../components/ModalMotivoRetrocesoCaptacion";
import PropiedadFormOverlay from "../components/PropiedadFormOverlay";
import ModalCompletarCamposAgente from "../components/ModalCompletarCamposAgente";
import ModalTransicionEtapa from "../components/ModalTransicionEtapa";
import ModalWhatsApp from "../components/ModalWhatsApp";
import ModalEmail from "../components/ModalEmail";
import { resolverIdiomaPropietarioCaptacion } from "../utils/captacionIdioma";
import ModalEnvioMasivoFrios from "../components/ModalEnvioMasivoFrios";
import CitaDesdeFrioModal from "../components/CitaDesdeFrioModal";
import { tipoInmuebleLegible, precioPipelineCard, refPipelineCard, agenteResponsableLabel, esCaptacionPrueba, etiquetaMultimediaPipeline, multimediaPendienteRevision, direccionPipelineCard } from "../utils/captacionDisplay";
import { resolveCaptacionMediaUrl } from "../services/captacionUpload";
import { crearCitaCaptacionAgenda, completarTareaActivaCaptacion } from "../utils/crearCitaCaptacionAgenda";
import { shouldOpenCompletarCamposAgente } from "../utils/captacionVisitaConcertadaModal";
import { fuenteVisibleEnLead, procedenciaChipStyle } from "../utils/procedenciaColor";
import { PipelineLeadMotionOverlay } from "../components/PipelineLeadMotionOverlay";
import { getTaskDisplayCode } from "../constants/taskUiColors";
import { PIPELINE_CARD_WIDTH_RATIO } from "../constants/pipelineLayout";
import { ANA_PIPELINE_MOTION_SPEED } from "../constants/pipelineMotion";

function parseExtrasPipeline(item) {
  if (typeof item?.datos_extras === "object" && item.datos_extras !== null) return item.datos_extras;
  try { return JSON.parse(item?.datos_extras || "{}"); } catch { return {}; }
}

/** Recoge strings de un objeto (datos_extras, etc.) para búsqueda libre. */
function flattenStringValues(val, out, depth = 0) {
  if (depth > 4 || val == null) return;
  if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
    const s = String(val).trim();
    if (s) out.push(s);
    return;
  }
  if (Array.isArray(val)) {
    val.forEach((v) => flattenStringValues(v, out, depth + 1));
    return;
  }
  if (typeof val === "object") {
    Object.values(val).forEach((v) => flattenStringValues(v, out, depth + 1));
  }
}

/** Campos de tarjeta + ficha (incluye datos_extras) para el buscador del modal Filtros. */
function camposTextoBusquedaPipeline(item) {
  const extras = parseExtrasPipeline(item);
  const out = [
    item?.nombre,
    item?.apellido,
    `${item?.nombre || ""} ${item?.apellido || ""}`.trim(),
    item?.telefono,
    item?.email,
    item?.operacion,
    item?.fuente,
    fuenteVisibleEnLead(item?.fuente),
    item?.calle,
    item?.numero,
    item?.poblacion,
    item?.poblacion_interes,
    item?.provincia,
    item?.codigo_postal,
    item?.notas,
    item?.agente_nombre,
    item?.quien_trabaja_nombre,
    item?.propiedad_ref,
    item?.ref,
    item?.referencia,
    item?.tarea_codigo,
    item?.titulo,
    item?.tipo_inmueble,
    item?.etapa,
    tipoInmuebleLegible(item, extras),
    refPipelineCard(item, extras),
    agenteResponsableLabel(item),
  ];
  const precio = precioPipelineCard(item, extras);
  if (precio != null) out.push(String(precio), new Intl.NumberFormat("es-ES").format(precio));
  flattenStringValues(extras, out);
  return out;
}

function itemCoincideBusquedaPipeline(item, query) {
  const q = String(query || "").trim();
  if (!q) return true;
  const qDigits = q.replace(/\D/g, "");
  if (qDigits.length >= 2) {
    const telDigits = String(item?.telefono || "").replace(/\D/g, "");
    if (telDigits && telDigits.includes(qDigits)) return true;
  }
  return textIncludesAny(camposTextoBusquedaPipeline(item), q);
}

// ============================================================
// ETAPAS
// ============================================================
const ETAPAS_LEADS = [
  { key: "leads",       label: "Leads",      color: "#eab308", gradient: "linear-gradient(135deg, #facc15, #eab308)" },
  { key: "atendidos",   label: "Atendidos",  color: "#f97316", gradient: "linear-gradient(135deg, #fb923c, #f97316)", esCliente: true },
  { key: "visitantes",  label: "Visitantes", color: "#ef4444", gradient: "linear-gradient(135deg, #f87171, #ef4444)", esCliente: true },
  { key: "calientes",   label: "Calientes",  color: "#ec4899", gradient: "linear-gradient(135deg, #f472b6, #ec4899)", esCliente: true },
  { key: "ventas",      label: "Ventas",     color: "#3b82f6", gradient: "linear-gradient(135deg, #60a5fa, #3b82f6)", esCliente: true },
];
const ETAPAS_LEADS_KEYS = new Set(ETAPAS_LEADS.map((e) => e.key));
/** Etapas que puede devolver GET /leads (incl. listas especiales). No coincide con captaciones. */
const LEAD_API_ETAPAS = new Set([...ETAPAS_LEADS_KEYS, "frios", "eliminados"]);

function itemsLookLikeLeadPipeline(items) {
  if (!items.length) return true;
  return items.every((it) => it && LEAD_API_ETAPAS.has(it.etapa));
}

const ETAPAS_CAPTACIONES = [
  { key: "precaptaciones",    label: "Precaptaciones",    color: "#eab308", gradient: "linear-gradient(135deg, #facc15, #eab308)" },
  { key: "contactados",       label: "Contactados",       color: "#f97316", gradient: "linear-gradient(135deg, #fb923c, #f97316)" },
  { key: "visita_concertada", label: "Visita Concertada", color: "#ef4444", gradient: "linear-gradient(135deg, #f87171, #ef4444)" },
  { key: "para_revisar",      label: "Para Revisar",      color: "#ec4899", gradient: "linear-gradient(135deg, #f472b6, #ec4899)" },
  { key: "captaciones",       label: "Captaciones",       color: "#3b82f6", gradient: "linear-gradient(135deg, #60a5fa, #3b82f6)" },
];

/** Preferencia de columnas finales expandida/plegada (sobrevive navegación y F5). */
const LS_PIPELINE_CAPTACIONES_COL = "pipeline_captaciones_col_expandida_v2";
const LS_PIPELINE_VENTAS_COL = "pipeline_ventas_col_expandida_v2";

function readPipelineColExpandida(key, fallback = false) {
  try {
    const v = localStorage.getItem(key);
    if (v == null) {
      // Migrar clave antigua (sin _v2) si existía
      const legacy = key.replace(/_v2$/, "");
      if (legacy !== key) {
        const old = localStorage.getItem(legacy);
        if (old != null) {
          localStorage.setItem(key, old === "1" || old === "true" ? "1" : "0");
          return old === "1" || old === "true";
        }
      }
      return fallback;
    }
    return v === "1" || v === "true";
  } catch {
    return fallback;
  }
}

function writePipelineColExpandida(key, value) {
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    /* ignore */
  }
}

const MAPA_ETAPAS_CAPTACIONES_FRONT_TO_BACK = {
  precaptaciones: "precaptacion",
  contactados: "contactado_cap",
  visita_concertada: "visita_cap",
  para_revisar: "revisar_cap",
  captaciones: "captacion",
};

const MAPA_ETAPAS_CAPTACIONES_BACK_TO_FRONT = Object.entries(MAPA_ETAPAS_CAPTACIONES_FRONT_TO_BACK)
  .reduce((acc, [front, back]) => ({ ...acc, [back]: front }), {});

const ETAPAS_ARCHIVO_CAPTACION = new Set(["eliminado", "eliminada", "fria", "frio"]);

/** Etapa Kanban previa al archivo (eliminadas/frías) para abrir el modal correcto. */
function etapaFrontendCaptacionArchivada(item) {
  const ex = parseExtrasPipeline(item);
  const candidatas = [
    ex.etapa_antes_archivo,
    ex.etapa_antes_eliminado,
    ex.etapa_antes_frio,
    item?.etapa,
  ];
  for (const c of candidatas) {
    const e = String(c || "").toLowerCase();
    if (!e || ETAPAS_ARCHIVO_CAPTACION.has(e)) continue;
    if (MAPA_ETAPAS_CAPTACIONES_BACK_TO_FRONT[e]) return MAPA_ETAPAS_CAPTACIONES_BACK_TO_FRONT[e];
  }
  return "precaptaciones";
}

const LABEL_ETAPA_LEAD_AL_ELIMINAR = {
  leads: "Leads",
  atendidos: "Atendidos",
  visitantes: "Visitantes",
  calientes: "Calientes",
  ventas: "Ventas",
  frios: "Fríos",
  frío: "Fríos",
  frio: "Fríos",
};

/** Etiqueta legible de la etapa en la que estaba el lead al eliminarse. */
function labelEtapaAlEliminarLead(item) {
  const ex = parseExtrasPipeline(item);
  const candidatas = [ex.etapa_antes_archivo, ex.etapa_antes_eliminado];
  const cur = String(item?.etapa || "").toLowerCase();
  // Soft-delete antiguo: activo=false pero etapa Kanban intacta
  if (cur && cur !== "eliminados" && cur !== "eliminado") candidatas.push(cur);
  for (const c of candidatas) {
    const e = String(c || "").toLowerCase().trim();
    if (!e || e === "eliminados" || e === "eliminado") continue;
    if (LABEL_ETAPA_LEAD_AL_ELIMINAR[e]) return LABEL_ETAPA_LEAD_AL_ELIMINAR[e];
    const found = ETAPAS_LEADS.find((x) => x.key === e);
    if (found) return found.label;
  }
  return "—";
}

function labelEtapaAlEliminarCaptacion(item) {
  const key = etapaFrontendCaptacionArchivada(item);
  return ETAPAS_CAPTACIONES.find((e) => e.key === key)?.label || key || "—";
}

// ============================================================
// MODAL TABLA EMBUDO
// ============================================================
const COL_STORAGE_KEY_EMBUDO = "vitalcasa_embudo_cols";
const COLS_LEADS = [
  { id: "nombre",       label: "Nombre",      width: 200 },
  { id: "telefono",     label: "Teléfono",    width: 140 },
  { id: "email",        label: "Email",       width: 220 },
  { id: "operacion",    label: "Operación",   width: 100 },
  { id: "fuente",       label: "Procedencia", width: 130 },
  { id: "agente_nombre",label: "Agente",      width: 160 },
  { id: "preagente_nombre", label: "Preagente asignado", width: 220 },
  { id: "created_at",   label: "Fecha / hora", width: 132 },
];

function getColsLeads(tipoModal = "embudo") {
  if (tipoModal !== "frios" && tipoModal !== "eliminados") return COLS_LEADS;
  // Fríos/Eliminados: Agente último + Agente anterior + Agente preasignado (sin Agente genérico).
  const cols = COLS_LEADS.filter(
    (c) => c.id !== "agente_nombre" && c.id !== "preagente_nombre"
  );
  const idxOperacion = cols.findIndex((c) => c.id === "operacion");
  if (idxOperacion !== -1) {
    cols.splice(idxOperacion + 1, 0, { id: "propiedad_ref", label: "Ref.", width: 100 });
  } else {
    cols.push({ id: "propiedad_ref", label: "Ref.", width: 100 });
  }
  const idxFecha = cols.findIndex((c) => c.id === "created_at");
  if (idxFecha !== -1) {
    cols[idxFecha] = { id: "created_at", label: "Fecha / Hora Vto", width: 132 };
    cols.splice(idxFecha + 1, 0, { id: "fecha_alta", label: "Fecha / Hora Alta", width: 132 });
  }
  const idxFuente = cols.findIndex((c) => c.id === "fuente");
  const insertAt = idxFuente !== -1 ? idxFuente + 1 : cols.length;
  cols.splice(
    insertAt,
    0,
    { id: "agente_ultimo_nombre", label: "Agente último", width: 150 },
    { id: "agente_anterior_nombre", label: "Agente anterior", width: 150 },
    { id: "preagente_nombre", label: "Agente preasignado", width: 200 }
  );
  if (tipoModal === "eliminados") {
    const idxPre = cols.findIndex((c) => c.id === "preagente_nombre");
    cols.splice(idxPre + 1, 0, { id: "etapa_al_eliminar", label: "Etapa", width: 130 });
  }
  return cols;
}

function refLeadEntrada(item = {}) {
  const directa = String(item.propiedad_ref || "").trim();
  if (directa) return directa;
  const ex =
    typeof item.datos_extras === "object" && item.datos_extras !== null
      ? item.datos_extras
      : (() => {
          try {
            return JSON.parse(item.datos_extras || "{}");
          } catch {
            return {};
          }
        })();
  return String(ex.referencia || ex.propiedad_ref || ex.referencia_propiedad || "").trim() || null;
}

function colsStorageKey(modo, tipoModal = "embudo") {
  const terminal = tipoModal === "frios" || tipoModal === "eliminados";
  return `${COL_STORAGE_KEY_EMBUDO}_${modo}${terminal ? "_terminal_v4" : ""}`;
}

/** Nombre del preagente / Agente preasignado (sin motivo). */
function nombreAgentePreasignado(item = {}) {
  const extras =
    typeof item.datos_extras === "object" && item.datos_extras !== null
      ? item.datos_extras
      : (() => {
          try {
            return JSON.parse(item.datos_extras || "{}");
          } catch {
            return {};
          }
        })();
  return String(
    item.preagente_nombre ||
      extras.preagente_nombre ||
      ""
  ).trim() || null;
}
const COLS_CAPTACIONES = [
  { id: "fotos",        label: "Fotos",        width: 90  },
  { id: "ref",          label: "Ref",          width: 100 },
  { id: "nombre",       label: "Nombre",       width: 200 },
  { id: "telefono",     label: "Teléfono",     width: 140 },
  { id: "email",        label: "Email",        width: 220 },
  { id: "operacion",    label: "Operación",    width: 100 },
  { id: "fuente",       label: "Procedencia",  width: 130 },
  { id: "tipo_inmueble",label: "Tipo",         width: 120 },
  { id: "precio_venta", label: "Precio venta", width: 120 },
  { id: "poblacion",    label: "Localidad",    width: 120 },
  { id: "area",         label: "Zona",         width: 130 },
  { id: "habitaciones", label: "Hab.",         width: 70  },
  { id: "piscina",      label: "Pisc.",        width: 70  },
  { id: "calle",        label: "Dirección",    width: 200 },
  { id: "agente_nombre",label: "Agente",       width: 160 },
  { id: "created_at",   label: "Fecha / hora", width: 132 },
];

function getColsCaptaciones(tipoModal = "embudo") {
  if (tipoModal !== "frios" && tipoModal !== "eliminados") return COLS_CAPTACIONES;
  const cols = COLS_CAPTACIONES.filter((c) => c.id !== "agente_nombre");
  const idxFecha = cols.findIndex((c) => c.id === "created_at");
  if (idxFecha !== -1) {
    cols[idxFecha] = { id: "created_at", label: "Fecha / Hora Vto", width: 132 };
    cols.splice(idxFecha + 1, 0, { id: "fecha_alta", label: "Fecha / Hora Alta", width: 132 });
  }
  const idxCalle = cols.findIndex((c) => c.id === "calle");
  const insertAt = idxCalle !== -1 ? idxCalle + 1 : cols.length;
  cols.splice(
    insertAt,
    0,
    { id: "agente_ultimo_nombre", label: "Agente último", width: 150 },
    { id: "agente_anterior_nombre", label: "Agente anterior", width: 150 },
    { id: "preagente_nombre", label: "Agente preasignado", width: 200 }
  );
  if (tipoModal === "eliminados") {
    const idxPre = cols.findIndex((c) => c.id === "preagente_nombre");
    const etapaAt = idxPre !== -1 ? idxPre + 1 : cols.length;
    cols.splice(etapaAt, 0, { id: "etapa_al_eliminar", label: "Etapa", width: 140 });
    cols.push(
      { id: "motivo_eliminacion", label: "Motivo", width: 240 },
      { id: "eliminado_por", label: "Eliminado por", width: 160 },
    );
  }
  return cols;
}

function cargarColsEmbudo(modo, tipoModal = "embudo") {
  const terminal = tipoModal === "frios" || tipoModal === "eliminados";
  const base = modo === "leads" ? getColsLeads(tipoModal) : getColsCaptaciones(tipoModal);
  const defaultConfig = base.map((c) => ({ id: c.id, visible: true }));
  try {
    const saved = localStorage.getItem(colsStorageKey(modo, tipoModal));
    if (saved) {
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed)) return defaultConfig;
      const baseIds = new Set(base.map((c) => c.id));
      const merged = [...parsed.filter((c) => baseIds.has(c.id))];
      const ids = new Set(merged.map((c) => c.id));
      for (const col of base) {
        if (!ids.has(col.id)) merged.push({ id: col.id, visible: true });
      }
      if (terminal) {
        return merged.map((c) => (c.id === "operacion" ? { ...c, visible: true } : c));
      }
      return merged;
    }
  } catch {}
  return defaultConfig;
}

// Mapeo columna → nodo de permisos (catálogo backend/src/config/permisosTree.js).
// Una columna sin "ver" se oculta por completo (tabla + menú de columnas).
const COL_PERM_NODE_LEADS = {
  nombre: "pl_leads_col_nombre",
  telefono: "pl_leads_col_telefono",
  email: "pl_leads_col_email",
  operacion: "pl_leads_col_operacion",
  fuente: "pl_leads_col_procedencia",
  agente_nombre: "pl_leads_col_agente",
  preagente_nombre: "pl_leads_col_agente",
  agente_ultimo_nombre: "pl_leads_col_agente_ultimo",
  created_at: "pl_leads_col_fecha",
  fecha_alta: "pl_leads_col_fecha",
  propiedad_ref: "pl_leads_col_ref",
  agente_anterior_nombre: "pl_leads_col_agente_anterior",
  etapa_al_eliminar: "pl_leads_col_etapa_al_eliminar",
};
const COL_PERM_NODE_CAPTACIONES = {
  fotos: "pl_capt_col_fotos",
  ref: "pl_capt_col_ref",
  nombre: "pl_capt_col_nombre",
  telefono: "pl_capt_col_telefono",
  email: "pl_capt_col_email",
  operacion: "pl_capt_col_operacion",
  fuente: "pl_capt_col_procedencia",
  tipo_inmueble: "pl_capt_col_tipo",
  precio_venta: "pl_capt_col_precio",
  poblacion: "pl_capt_col_localidad",
  area: "pl_capt_col_zona",
  habitaciones: "pl_capt_col_hab",
  piscina: "pl_capt_col_piscina",
  calle: "pl_capt_col_direccion",
  agente_nombre: "pl_capt_col_agente",
  agente_ultimo_nombre: "pl_capt_col_agente_ultimo",
  preagente_nombre: "pl_capt_col_agente",
  created_at: "pl_capt_col_fecha",
  fecha_alta: "pl_capt_col_fecha",
  agente_anterior_nombre: "pl_capt_col_agente_anterior",
  etapa_al_eliminar: "pl_capt_col_etapa_al_eliminar",
  motivo_eliminacion: "pl_capt_col_motivo_eliminacion",
  eliminado_por: "pl_capt_col_eliminado_por",
};
function filtrarColumnasPorPermiso(cols, modo, puede) {
  const mapa = modo === "leads" ? COL_PERM_NODE_LEADS : COL_PERM_NODE_CAPTACIONES;
  return cols.filter((col) => {
    const nodeId = mapa[col.id];
    if (!nodeId) return true;
    if (puede(nodeId, "ver")) return true;
    // Ampliar: nodo nuevo «Agente último» hereda ver de agente / agente anterior
    // hasta que el rol lo tenga explícito en el árbol de permisos.
    if (col.id === "agente_ultimo_nombre") {
      const altAgente = modo === "leads" ? "pl_leads_col_agente" : "pl_capt_col_agente";
      const altAnterior = modo === "leads" ? "pl_leads_col_agente_anterior" : "pl_capt_col_agente_anterior";
      return puede(altAgente, "ver") || puede(altAnterior, "ver");
    }
    return false;
  });
}

function ModalTablaEmbudo({ etapa, items, modo, theme, onClose, tipoModal = "embudo", onClickItem, onOpenLead, onEliminar, onRestaurar, onEnviarAActivos, restaurarLabel = "↩ Restaurar seleccionados", onEliminarPermanente, crearCitaDesdeFrio = false, usuario = null, onCitaCreada, onAbrirVistaPreviaRef, cerrarAlAbrirItem = true }) {
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const todasColumnas = filtrarColumnasPorPermiso(
    modo === "leads" ? getColsLeads(tipoModal) : getColsCaptaciones(tipoModal),
    modo,
    puede,
  );
  const [colConfig, setColConfig] = useState(() => cargarColsEmbudo(modo, tipoModal));
  const [mostrarCols, setMostrarCols] = useState(false);
  const [dragCol, setDragCol] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [modalWA, setModalWA] = useState(null);
  const [modalEmail, setModalEmail] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [eliminando, setEliminando] = useState(false);
  const [eliminandoPermanente, setEliminandoPermanente] = useState(false);
  const [procesandoFrios, setProcesandoFrios] = useState(false);
  const [procesandoActivos, setProcesandoActivos] = useState(false);
  const [citaLead, setCitaLead] = useState(null);
  const [modalCitaFrio, setModalCitaFrio] = useState(false);
  const [modalEnvioMasivo, setModalEnvioMasivo] = useState(false);
  const [leadsEnvioMasivo, setLeadsEnvioMasivo] = useState([]);

  // Permisos de filtros de la tabla-embudo (columnas ya filtradas arriba en `todasColumnas`).
  const pref = modo === "leads" ? "pl_leads" : "pl_capt";
  const puedeFiltroAgente = puede(`${pref}_filtro_agente`, "ver");
  const puedeFiltroFuente = puede(`${pref}_filtro_procedencia`, "ver");
  const puedeFiltroFecha = puede(`${pref}_filtro_fecha`, "ver");
  const puedeFiltroBusqueda = puede(`${pref}_filtro_busqueda`, "ver");

  // Permisos de botones del head (Fríos / Eliminados). En leads cuelgan de la etapa
  // en el árbol; agentes no reactivan (Restaurar / Enviar a Activos) salvo vía cita.
  const puedeMasivaRestaurar = puede(`${pref}_masiva_restaurar`, "ver");
  const puedeMasivaEnviarFrios = modo === "leads" ? puede("pl_leads_masiva_enviar_frios", "ver") : puedeMasivaRestaurar;
  const puedeMasivaEliminar = puede(`${pref}_masiva_eliminar`, "ver");
  const puedeMasivaEliminarPerm = puede(`${pref}_masiva_eliminar_perm`, "ver");
  const puedeMasivaActivos = modo === "leads" ? puede("pl_leads_masiva_activos", "ver") : true;
  const puedeMasivaEnvioMasivo = modo === "leads" ? puede("pl_leads_masiva_envio_masivo", "ver") : true;
  const puedeMasivaCitaFrio = modo === "leads" ? puede("pl_leads_masiva_cita_frio", "ver") : true;

  // Filtros
  const [filtroAgente, setFiltroAgente] = useState("");
  const [filtroAgenteUltimo, setFiltroAgenteUltimo] = useState("");
  const [filtroAgenteAnterior, setFiltroAgenteAnterior] = useState("");
  const [filtroAgentePreasignado, setFiltroAgentePreasignado] = useState("");
  const [filtroFuente, setFiltroFuente] = useState("");
  const [filtroFecha, setFiltroFecha] = useState("todos");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [filtroBusqueda, setFiltroBusqueda] = useState("");
  const fechaDesdeRef = useRef(null);

  const esFrios = tipoModal === "frios";
  const esTerminalEmbudo = esFrios || tipoModal === "eliminados";
  const agentes = [...new Set(items.map((i) => i.agente_nombre).filter(Boolean))].sort();
  const agentesUltimo = [...new Set(items.map((i) => i.agente_ultimo_nombre).filter(Boolean))].sort();
  const agentesAnterior = [...new Set(items.map((i) => i.agente_anterior_nombre).filter(Boolean))].sort();
  const agentesPreasignado = [...new Set(items.map((i) => nombreAgentePreasignado(i)).filter(Boolean))].sort();
  const fuentes  = [...new Set(items.map(i => i.fuente).filter(Boolean))].sort();

  const soloDigitos = (s) => String(s || "").replace(/\D/g, "");

  const filtrarPorFecha = (item) => {
    const ref = fechaReferenciaPipeline(item, { tipoModal });
    const d = ref ? new Date(ref) : null;
    return filtroFechaPipelineMatch(filtroFecha, d, new Date(), fechaDesde, fechaHasta);
  };

  const refBusquedaEmbudo = (i) => {
    if (modo === "leads") return refLeadEntrada(i) || "";
    return String(i.ref || i.referencia || "").trim();
  };

  const cumpleBusquedaEmbudo = (i) => {
    const q = filtroBusqueda.trim();
    if (!q) return true;
    const qDigits = soloDigitos(q);
    if (qDigits.length >= 2 && i.telefono && soloDigitos(i.telefono).includes(qDigits)) return true;
    return textIncludesAny(
      [
        `${i.nombre || ""} ${i.apellido || ""}`.trim(),
        i.telefono,
        i.email,
        i.operacion,
        refBusquedaEmbudo(i),
      ],
      q,
    );
  };

  const itemsFiltrados = items.filter((i) => {
    const matchAgente = esTerminalEmbudo
      ? (
          (!filtroAgenteUltimo || i.agente_ultimo_nombre === filtroAgenteUltimo) &&
          (!filtroAgenteAnterior || i.agente_anterior_nombre === filtroAgenteAnterior) &&
          (!filtroAgentePreasignado || nombreAgentePreasignado(i) === filtroAgentePreasignado)
        )
      : (!filtroAgente || i.agente_nombre === filtroAgente);
    return (
      matchAgente &&
      (!filtroFuente || i.fuente === filtroFuente) &&
      filtrarPorFecha(i) &&
      cumpleBusquedaEmbudo(i)
    );
  });

  const colsVisibles = colConfig
    .filter(cc => cc.visible !== false)
    .map(cc => todasColumnas.find(col => col.id === cc.id))
    .filter(Boolean);

  const toggleCol = (id) => {
    const nueva = colConfig.map(cc => cc.id === id ? { ...cc, visible: !cc.visible } : cc);
    setColConfig(nueva);
    localStorage.setItem(colsStorageKey(modo, tipoModal), JSON.stringify(nueva));
  };

  /** Reordenar como Propiedades → ⚙️ Columnas (arrastra ⠿). */
  const onDragStartCol = (e, id) => {
    setDragCol(id);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", id);
    } catch {
      /* ignore */
    }
  };
  const onDragOverCol = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== dragCol) setDragOverCol(id);
  };
  const onDropCol = (e, id) => {
    e.preventDefault();
    if (!dragCol || dragCol === id) {
      setDragCol(null);
      setDragOverCol(null);
      return;
    }
    const from = colConfig.findIndex((c) => c.id === dragCol);
    const to = colConfig.findIndex((c) => c.id === id);
    if (from < 0 || to < 0) {
      setDragCol(null);
      setDragOverCol(null);
      return;
    }
    const nueva = [...colConfig];
    const [moved] = nueva.splice(from, 1);
    nueva.splice(to, 0, moved);
    setColConfig(nueva);
    localStorage.setItem(colsStorageKey(modo, tipoModal), JSON.stringify(nueva));
    setDragCol(null);
    setDragOverCol(null);
  };

  const abrirLead = (item) => {
    if (modo === "leads") {
      if (onOpenLead) {
        onOpenLead(item);
        if (cerrarAlAbrirItem) onClose();
      } else if (onClickItem) {
        onClickItem(item);
      }
      return;
    }
    if (onClickItem) {
      onClickItem(item);
      if (cerrarAlAbrirItem) onClose();
    }
  };

  const contactoDesde = (item) => ({
    nombre:    `${item.nombre || ""} ${item.apellido || ""}`.trim(),
    telefono:  item.telefono || "",
    email:     item.email    || "",
    iniciales: ((item.nombre || "?")[0] + (item.apellido || "?")[0]).toUpperCase(),
  });

  const selectStyle = {
    padding: "5px 10px", borderRadius: 7, border: `1px solid ${c.border}`,
    background: c.bgCard, color: c.textSecondary, fontSize: 12,
    fontFamily: "inherit", cursor: "pointer", outline: "none",
  };
  /** Título fijo fuera del select; dentro del desplegable solo van los valores. */
  const filtroConTituloStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 4px 3px 10px",
    borderRadius: 7,
    border: `1px solid ${c.border}`,
    background: c.bgCard,
  };
  const filtroTituloStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: c.textMuted,
    whiteSpace: "nowrap",
    flexShrink: 0,
  };
  const selectEnTituloStyle = {
    ...selectStyle,
    border: "none",
    background: "transparent",
    padding: "2px 8px 2px 0",
    minWidth: 0,
  };
  const FiltroConTitulo = ({ titulo, children }) => (
    <div style={filtroConTituloStyle}>
      <span style={filtroTituloStyle}>{titulo}:</span>
      {children}
    </div>
  );
  const dateInputStyle = {
    ...selectStyle,
    width: 148,
    minWidth: 148,
    color: c.textPrimary,
    background: isDark ? c.bgPanel : "#fff",
    colorScheme: isDark ? "dark" : "light",
    cursor: "pointer",
  };
  const inputFiltroStyle = {
    padding: "5px 10px", borderRadius: 7, border: `1px solid ${c.border}`,
    background: c.bgInput || c.bgCard, color: c.textPrimary, fontSize: 12,
    fontFamily: "inherit", cursor: "text", outline: "none", flex: "1 1 220px", minWidth: 220, maxWidth: 420,
    boxSizing: "border-box",
  };

  const renderCelda = (item, colId) => {
    if (colId === "fotos") {
      // Misma lógica que TarjetaCaptacion: portada reportaje (slim) o identificación; total real en fotos_reportaje_count.
      const fotosIdentificacion = Array.isArray(item.fotos)
        ? item.fotos
        : (item.fotos ? (() => { try { return JSON.parse(item.fotos); } catch { return []; } })() : []);
      const fotosReportaje = Array.isArray(item.fotos_reportaje)
        ? item.fotos_reportaje
        : (item.fotos_reportaje ? (() => { try { return JSON.parse(item.fotos_reportaje); } catch { return []; } })() : []);
      const portadaRaw = fotosReportaje.length > 0
        ? fotosReportaje[0]
        : (fotosIdentificacion.length > 0 ? fotosIdentificacion[0] : null);
      const portadaResolved = portadaRaw ? resolveCaptacionMediaUrl(portadaRaw) : null;
      const portadaSrc = portadaResolved
        ? (String(portadaResolved).startsWith("http") ? proxyFoto(portadaResolved) : portadaResolved)
        : null;
      const totalFotos = Math.max(
        Number(item.fotos_reportaje_count) || 0,
        fotosReportaje.length + fotosIdentificacion.length,
      );
      const canClickFoto = modo === "captaciones" && onClickItem;
      return (
        <div onClick={canClickFoto ? (e) => { e.stopPropagation(); abrirLead(item); } : undefined}
          style={{ position: "relative", width: 70, height: 46, cursor: canClickFoto ? "pointer" : "default" }}>
          {portadaSrc ? (
            <img src={portadaSrc} alt="" style={{ width: 70, height: 46, objectFit: "cover", borderRadius: 6, display: "block" }} />
          ) : (
            <div style={{ width: 70, height: 46, borderRadius: 6, background: isDark ? "#1e293b" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c.textPlaceholder} strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            </div>
          )}
          {totalFotos > 0 && (
            <span style={{ position: "absolute", bottom: 3, right: 3, fontSize: 10, fontWeight: 700, background: "rgba(0,0,0,0.6)", color: "#fff", borderRadius: 4, padding: "1px 4px" }}>{totalFotos}</span>
          )}
        </div>
      );
    }
    if (colId === "nombre") {
      const canClickNombre = modo === "leads" || (modo === "captaciones" && onClickItem);
      const nom = `${item.nombre || ""} ${item.apellido || ""}`.trim();
      const label = nom || (item.telefono ? `(Sin nombre) · ${item.telefono}` : "(Sin nombre)");
      return (
        <span onClick={(e) => { e.stopPropagation(); if (modo === "leads") abrirLead(item); else if (onClickItem) abrirLead(item); }}
          style={{ fontWeight: 600, color: nom ? "#fff" : c.textMuted, cursor: canClickNombre ? "pointer" : "default", textDecoration: "underline", textDecorationColor: "transparent" }}
          onMouseEnter={e => { if (canClickNombre) e.currentTarget.style.textDecorationColor = "currentColor"; }}
          onMouseLeave={e => e.currentTarget.style.textDecorationColor = "transparent"}>
          {label}
        </span>
      );
    }
    if (colId === "telefono") {
      if (!item.telefono) return "—";
      return (
        <span onClick={(e) => { e.stopPropagation(); setModalWA(item); }}
          style={{ color: "#16a34a", cursor: "pointer", fontWeight: 500 }} title="Enviar WhatsApp">
          {item.telefono}
        </span>
      );
    }
    if (colId === "email") {
      if (!item.email) return "—";
      return (
        <span onClick={(e) => { e.stopPropagation(); setModalEmail(item); }}
          style={{ color: "#f97316", cursor: "pointer" }}
          onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
          onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>
          {item.email}
        </span>
      );
    }
    if (colId === "operacion") {
      if (!item.operacion) return "—";
      const col = item.operacion.toLowerCase() === "alquiler" ? c.accentPink : c.accentGreen;
      const bg  = item.operacion.toLowerCase() === "alquiler" ? (c.accentPinkLight || "#fce7f3") : (c.accentGreenLight || "#f0fdf4");
      return <span style={{ fontSize: 11, fontWeight: 700, color: col, background: bg, padding: "2px 7px", borderRadius: 5 }}>{item.operacion}</span>;
    }
    if (colId === "fuente") {
      const fuente = fuenteVisibleEnLead(item.fuente);
      if (!fuente) return "—";
      return (
        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, whiteSpace: "nowrap", ...procedenciaChipStyle(fuente) }}>
          {fuente}
        </span>
      );
    }
    if (colId === "agente_nombre") {
      const agente = agenteResponsableLabel(item);
      const ana = agente === "Ana";
      return (
        <span style={{ color: ana ? "#10b981" : (isDark ? "#60a5fa" : c.accentBlue), fontWeight: 500 }}>
          {agente || "—"}
        </span>
      );
    }
    if (colId === "preagente_nombre") {
      const extras =
        typeof item.datos_extras === "object" && item.datos_extras !== null
          ? item.datos_extras
          : (() => {
              try {
                return JSON.parse(item.datos_extras || "{}");
              } catch {
                return {};
              }
            })();
      const nombre = String(
        item.preagente_nombre ||
          extras.preagente_nombre ||
          ""
      ).trim();
      const motivo = String(
        item.preagente_motivo ||
          extras.preagente_motivo ||
          extras.asignacion_agente?.motivo ||
          ""
      ).trim();
      // Embudo activo: nombre + motivo. Fríos/Eliminados: solo nombre.
      if (esTerminalEmbudo) {
        if (!nombre) return "—";
        return (
          <span
            title={motivo ? `${nombre} — ${motivo}` : nombre}
            style={{ color: "#16a34a", fontWeight: 500 }}
          >
            {nombre}
          </span>
        );
      }
      if (!nombre && !motivo) return "—";
      const texto = nombre
        ? (motivo ? `${nombre} — ${motivo}` : nombre)
        : `Sin comercial — ${motivo}`;
      return (
        <span
          title={texto}
          style={{
            color: "#16a34a",
            fontWeight: 500,
            fontSize: 12,
            display: "block",
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {texto}
        </span>
      );
    }
    if (colId === "agente_ultimo_nombre") {
      const ultimo =
        item.agente_ultimo_nombre ||
        (modo === "captaciones" ? item.agente_nombre : null) ||
        null;
      if (!ultimo) return "—";
      const esAna = /^ana$/i.test(String(ultimo).trim()) || /asistente virtual/i.test(String(ultimo));
      return (
        <span style={{ color: esAna ? "#10b981" : (isDark ? "#60a5fa" : c.accentBlue), fontWeight: 500 }}>
          {ultimo}
        </span>
      );
    }
    if (colId === "agente_anterior_nombre") {
      const ultimo = item.agente_ultimo_nombre || null;
      const prev = item.agente_anterior_nombre;
      if (!prev) return "—";
      if (ultimo && String(prev).toLowerCase() === String(ultimo).toLowerCase()) return "—";
      return <span style={{ color: "#16a34a", fontWeight: 500 }}>{prev}</span>;
    }
    if (colId === "etapa_al_eliminar") {
      const label = modo === "captaciones"
        ? labelEtapaAlEliminarCaptacion(item)
        : labelEtapaAlEliminarLead(item);
      if (!label || label === "—") return "—";
      return <span style={{ fontWeight: 600 }}>{label}</span>;
    }
    if (colId === "motivo_eliminacion") {
      const m = String(item.motivo_eliminacion || "").trim();
      if (!m) return "—";
      return <span title={m} style={{ fontWeight: 500 }}>{m}</span>;
    }
    if (colId === "eliminado_por") {
      const quien = String(item.eliminado_por || "").trim();
      if (!quien) return "—";
      return <span style={{ fontWeight: 500 }}>{quien}</span>;
    }
    if (colId === "propiedad_ref") {
      const ref = refLeadEntrada(item);
      if (!ref) return "—";
      if (!onAbrirVistaPreviaRef) {
        return <span style={{ fontWeight: 700, color: "#ffDE21" }}>{ref}</span>;
      }
      return (
        <span
          role="link"
          tabIndex={0}
          title="Ver vista previa de la propiedad"
          onClick={(e) => onAbrirVistaPreviaRef(e, ref)}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onAbrirVistaPreviaRef(e, ref);
          }}
          style={{
            fontWeight: 700,
            color: "#ffDE21",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {ref}
        </span>
      );
    }
    if (colId === "fecha_alta") {
      return formatCeldaFechaHora(item.created_at, c);
    }
    if (colId === "created_at") {
      // Embudo: alta/cita. Fríos/Eliminados: momento de archivo / vencimiento (Vto).
      return formatCeldaFechaHora(fechaReferenciaPipeline(item, { tipoModal }), c);
    }
    if (colId === "ref") {
      const ex = typeof item.datos_extras === "object" && item.datos_extras !== null ? item.datos_extras : (() => { try { return JSON.parse(item.datos_extras || "{}"); } catch { return {}; } })();
      const r = refPipelineCard(item, ex);
      return r ? <span style={{ fontWeight: 700, color: "#ffDE21" }}>{r}</span> : "—";
    }
    if (colId === "precio_venta") {
      const ex = typeof item.datos_extras === "object" && item.datos_extras !== null ? item.datos_extras : (() => { try { return JSON.parse(item.datos_extras || "{}"); } catch { return {}; } })();
      const num = precioPipelineCard(item, ex);
      if (num == null) return "—";
      const col = item.operacion?.toLowerCase() === "alquiler" ? c.accentPink : c.accentGreen;
      return <span style={{ fontWeight: 700, color: col }}>{new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(num)} €</span>;
    }
    if (colId === "poblacion") return item.poblacion || item.poblacion_interes || "—";
    if (colId === "area") {
      const ex = typeof item.datos_extras === "object" && item.datos_extras !== null ? item.datos_extras : (() => { try { return JSON.parse(item.datos_extras || "{}"); } catch { return {}; } })();
      return item.zona || item.area || ex.area || "—";
    }
    if (colId === "habitaciones") {
      const ex = typeof item.datos_extras === "object" && item.datos_extras !== null ? item.datos_extras : (() => { try { return JSON.parse(item.datos_extras || "{}"); } catch { return {}; } })();
      const v = item.habitaciones ?? item.habitaciones_min ?? ex.habitaciones ?? ex.num_habitaciones;
      return v != null ? String(v) : "—";
    }
    if (colId === "piscina") {
      const ex = typeof item.datos_extras === "object" && item.datos_extras !== null ? item.datos_extras : (() => { try { return JSON.parse(item.datos_extras || "{}"); } catch { return {}; } })();
      const v = item.piscina ?? ex.piscina;
      if (v == null || v === "" || v === false || v === "0" || v === 0) return "—";
      return <span style={{ color: "#06b6d4", fontWeight: 600 }}>✓</span>;
    }
    if (colId === "calle") return item.calle ? `${item.calle}${item.numero ? ` ${item.numero}` : ""}${item.poblacion ? ` · ${item.poblacion}` : ""}` : "—";
    if (colId === "tipo_inmueble") {
      const ex =
        typeof item.datos_extras === "object" && item.datos_extras !== null
          ? item.datos_extras
          : (() => {
              try {
                return JSON.parse(item.datos_extras || "{}");
              } catch {
                return {};
              }
            })();
      return tipoInmuebleLegible(item, ex) || "—";
    }
    return item[colId] || "—";
  };

  return (
    <>
      <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={esTerminalEmbudo ? undefined : onClose}
      >
        <div style={{ background: c.bgPanel, borderRadius: 16, width: esTerminalEmbudo ? "98vw" : "95vw", maxWidth: esTerminalEmbudo ? 1880 : 1500, maxHeight: "88vh", display: "flex", flexDirection: "column", border: `1px solid ${c.border}`, boxShadow: "0 25px 60px rgba(0,0,0,0.3)" }}
          onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div style={{ padding: "12px 20px", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: etapa.color, flexShrink: 0 }} />
              <span style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary }}>{etapa.label}</span>
              <span style={{ fontSize: 12, padding: "2px 10px", borderRadius: 20, background: `${etapa.color}20`, color: etapa.color, fontWeight: 700 }}>
                {itemsFiltrados.length}{itemsFiltrados.length !== items.length ? ` / ${items.length}` : ""}
              </span>
              {selectedIds.size > 0 && (
                <span style={{ fontSize: 12, color: c.textMuted }}>{selectedIds.size} seleccionado{selectedIds.size > 1 ? "s" : ""}</span>
              )}
              {(procesandoFrios || procesandoActivos) && (
                <span style={{ fontSize: 12, color: c.textMuted, fontStyle: "italic" }}>
                  {procesandoActivos ? "Asignando agente y reactivando lead…" : "Enviando a Fríos…"}
                </span>
              )}
              {selectedIds.size > 0 && onRestaurar && (esFrios ? puedeMasivaRestaurar : puedeMasivaEnviarFrios) && (
                <button
                  disabled={procesandoFrios || procesandoActivos || eliminando || eliminandoPermanente}
                  onClick={async () => {
                    setProcesandoFrios(true);
                    try {
                      await onRestaurar([...selectedIds]);
                      setSelectedIds(new Set());
                    } finally {
                      setProcesandoFrios(false);
                    }
                  }}
                  style={{ padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: c.accentBlueLight, color: isDark ? "#60a5fa" : c.accentBlue, border: `1px solid ${c.accentBlueBorder}`, cursor: (procesandoFrios || procesandoActivos) ? "wait" : "pointer", fontFamily: "inherit", opacity: (procesandoFrios || procesandoActivos) ? 0.65 : 1 }}>
                  {procesandoFrios ? "Enviando a Fríos…" : restaurarLabel}
                </button>
              )}
              {selectedIds.size > 0 && onEnviarAActivos && puedeMasivaActivos && (
                <button
                  disabled={procesandoActivos || procesandoFrios || eliminando || eliminandoPermanente}
                  onClick={async () => {
                    setProcesandoActivos(true);
                    try {
                      await onEnviarAActivos([...selectedIds]);
                      setSelectedIds(new Set());
                    } finally {
                      setProcesandoActivos(false);
                    }
                  }}
                  style={{ padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: isDark ? "#1e3a2f" : "#ecfdf5", color: isDark ? "#86efac" : "#166534", border: `1px solid ${isDark ? "#166534" : "#86efac"}`, cursor: procesandoActivos ? "wait" : "pointer", fontFamily: "inherit", opacity: procesandoActivos ? 0.65 : 1 }}>
                  {procesandoActivos ? "Enviando a Activos…" : "Enviar a Activos"}
                </button>
              )}
              {selectedIds.size > 0 && esFrios && modo === "leads" && puedeMasivaEnvioMasivo && (
                <button
                  type="button"
                  disabled={procesandoFrios || procesandoActivos || eliminando || eliminandoPermanente}
                  onClick={() => {
                    setLeadsEnvioMasivo(items.filter((i) => selectedIds.has(i.id)));
                    setModalEnvioMasivo(true);
                  }}
                  style={{
                    padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                    background: isDark ? "#14532d" : "#dcfce7", color: isDark ? "#86efac" : "#15803d",
                    border: `1px solid ${isDark ? "#166534" : "#86efac"}`, cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  💬 WhatsApp / Email
                </button>
              )}
              {selectedIds.size === 1 && crearCitaDesdeFrio && puedeMasivaCitaFrio && (
                <button
                  onClick={async () => {
                    if (!usuario?.agente_id) {
                      await dialogs.alert("Inicia sesión como agente para crear la cita y reasignar el lead.");
                      return;
                    }
                    const lead = items.find((i) => i.id === [...selectedIds][0]);
                    if (!lead) return;
                    setCitaLead(lead);
                    setModalCitaFrio(true);
                  }}
                  style={{ padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: isDark ? "#1e3a2f" : "#ecfdf5", color: isDark ? "#86efac" : "#166534", border: `1px solid ${isDark ? "#166534" : "#86efac"}`, cursor: "pointer", fontFamily: "inherit" }}
                >
                  📅 Crear cita
                </button>
              )}
              {selectedIds.size > 0 && onEliminar && puedeMasivaEliminar && (
                <button onClick={async () => {
                    if (!(await dialogs.confirm(`¿Eliminar ${selectedIds.size} registro${selectedIds.size > 1 ? "s" : ""}?`))) return;
                    setEliminando(true);
                    try {
                      await onEliminar([...selectedIds]);
                      setSelectedIds(new Set());
                    } catch (e) {
                      console.error(e);
                    } finally {
                      setEliminando(false);
                    }
                  }}
                  disabled={eliminando}
                  style={{ padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: eliminando ? c.bgCard : "#fef2f2", color: eliminando ? c.textMuted : "#ef4444", border: "1px solid #fecaca", cursor: "pointer", fontFamily: "inherit", opacity: eliminando ? 0.6 : 1 }}>
                  {eliminando ? "Eliminando..." : "🗑 Eliminar"}
                </button>
              )}
              {selectedIds.size > 0 && onEliminarPermanente && puedeMasivaEliminarPerm && (
                <button
                  onClick={async () => {
                    const n = selectedIds.size;
                    const texto =
                      modo === "captaciones"
                        ? `Vas a ELIMINAR DEFINITIVAMENTE ${n} captación${n > 1 ? "es" : ""} del sistema.\n\n` +
                          "• NO podrás recuperarlas desde el CRM (acción irreversible).\n" +
                          "• Se borrarán fotos, vídeos, documentos, tareas, citas e historial vinculados.\n\n" +
                          "¿Continuar?"
                        : `Vas a ELIMINAR DEFINITIVAMENTE ${n} lead${n > 1 ? "s" : ""} del sistema.\n\n` +
                          "• NO podrás recuperarlos desde el CRM (acción irreversible).\n" +
                          "• Se borrarán tareas, citas y conversaciones Ana vinculadas al lead.\n" +
                          "• El historial conservará entradas pero sin ficha de lead.\n" +
                          "• El cliente vinculado, si existe, no se elimina.\n\n" +
                          "¿Continuar?";
                    if (!(await dialogs.confirm(texto))) return;
                    if (!(await dialogs.confirm("Última confirmación: ¿borrar para siempre?"))) return;
                    setEliminandoPermanente(true);
                    try {
                      await onEliminarPermanente([...selectedIds]);
                      setSelectedIds(new Set());
                    } catch (e) {
                      console.error(e);
                    } finally {
                      setEliminandoPermanente(false);
                    }
                  }}
                  disabled={eliminandoPermanente}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 700,
                    background: eliminandoPermanente ? c.bgCard : isDark ? "#450a0a" : "#fef2f2",
                    color: "#b91c1c",
                    border: "2px solid #b91c1c",
                    cursor: eliminandoPermanente ? "wait" : "pointer",
                    fontFamily: "inherit",
                    opacity: eliminandoPermanente ? 0.6 : 1,
                  }}
                >
                  {eliminandoPermanente ? "Eliminando..." : "⛔ Eliminar permanentemente"}
                </button>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ position: "relative" }}>
                <button onClick={() => setMostrarCols(!mostrarCols)}
                  style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${c.border}`, background: "transparent", color: c.textSecondary, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                  ⚙️ Columnas
                </button>
                {mostrarCols && (
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: 10, padding: 12, zIndex: 10, width: 240, maxHeight: 360, display: "flex", flexDirection: "column", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, flexShrink: 0 }}>
                      Columnas visibles (arrastra para reordenar)
                    </p>
                    <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
                      {colConfig.map((cc) => {
                        const colDef = todasColumnas.find((col) => col.id === cc.id);
                        if (!colDef) return null;
                        return (
                          <div
                            key={cc.id}
                            draggable
                            onDragStart={(e) => onDragStartCol(e, cc.id)}
                            onDragOver={(e) => onDragOverCol(e, cc.id)}
                            onDrop={(e) => onDropCol(e, cc.id)}
                            onDragEnd={() => {
                              setDragCol(null);
                              setDragOverCol(null);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 4px",
                              borderRadius: 6,
                              cursor: "grab",
                              background: dragOverCol === cc.id ? c.accentBlueLight : "transparent",
                              opacity: dragCol === cc.id ? 0.55 : 1,
                            }}
                          >
                            <span style={{ fontSize: 12, color: c.textMuted, userSelect: "none", lineHeight: 1 }} title="Arrastra para reordenar">
                              ⠿
                            </span>
                            <input
                              type="checkbox"
                              checked={cc.visible !== false}
                              onChange={() => toggleCol(cc.id)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ cursor: "pointer" }}
                            />
                            <span style={{ fontSize: 13, color: c.textSecondary }}>{colDef.label}</span>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => setMostrarCols(false)}
                      style={{ marginTop: 8, width: "100%", fontSize: 12, color: c.accentBlue, background: "none", border: "none", cursor: "pointer", flexShrink: 0 }}
                    >
                      Cerrar
                    </button>
                  </div>
                )}
              </div>
              <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, color: c.textMuted, cursor: "pointer", padding: "2px 6px" }}>✕</button>
            </div>
          </div>

          {/* Filtros */}
          <div style={{ padding: "10px 20px", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", flexShrink: 0, background: isDark ? c.bgPanel : "#fafafa" }}>
            {puedeFiltroAgente && !esTerminalEmbudo && (
            <FiltroConTitulo titulo="Agente">
              <select value={filtroAgente} onChange={e => setFiltroAgente(e.target.value)} style={selectEnTituloStyle}>
                <option value="">todos</option>
                {agentes.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </FiltroConTitulo>
            )}
            {puedeFiltroAgente && esTerminalEmbudo && (
              <>
                <FiltroConTitulo titulo="Agente último">
                  <select value={filtroAgenteUltimo} onChange={(e) => setFiltroAgenteUltimo(e.target.value)} style={selectEnTituloStyle}>
                    <option value="">todos</option>
                    {agentesUltimo.map((a) => <option key={`u-${a}`} value={a}>{a}</option>)}
                  </select>
                </FiltroConTitulo>
                <FiltroConTitulo titulo="Agente anterior">
                  <select value={filtroAgenteAnterior} onChange={(e) => setFiltroAgenteAnterior(e.target.value)} style={selectEnTituloStyle}>
                    <option value="">todos</option>
                    {agentesAnterior.map((a) => <option key={`a-${a}`} value={a}>{a}</option>)}
                  </select>
                </FiltroConTitulo>
                <FiltroConTitulo titulo="Agente preasignado">
                  <select value={filtroAgentePreasignado} onChange={(e) => setFiltroAgentePreasignado(e.target.value)} style={selectEnTituloStyle}>
                    <option value="">todos</option>
                    {agentesPreasignado.map((a) => <option key={`p-${a}`} value={a}>{a}</option>)}
                  </select>
                </FiltroConTitulo>
              </>
            )}
            {puedeFiltroFuente && (
            <FiltroConTitulo titulo="Procedencia">
              <select value={filtroFuente} onChange={e => setFiltroFuente(e.target.value)} style={selectEnTituloStyle}>
                <option value="">todas</option>
                {fuentes.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </FiltroConTitulo>
            )}
            {puedeFiltroFecha && (
            <FiltroConTitulo titulo="Fecha">
              <select
                value={filtroFecha}
                onChange={(e) => {
                  const v = e.target.value;
                  setFiltroFecha(v);
                  if (v === "custom") {
                    setTimeout(() => abrirPickerFecha(fechaDesdeRef.current), 50);
                  } else {
                    setFechaDesde("");
                    setFechaHasta("");
                  }
                }}
                style={selectEnTituloStyle}
              >
                <option value="todos">cualquiera</option>
                <option value="hoy">Hoy</option>
                <option value="esta_semana">Esta semana</option>
                <option value="este_mes">Este mes</option>
                <option value="semana_pasada">Semana pasada</option>
                <option value="mes_pasado">Mes pasado</option>
                <option value="7d">Últimos 7 días</option>
                <option value="30d">Últimos 30 días</option>
                <option value="90d">Últimos 90 días</option>
                <option value="este_anio">Este año</option>
                <option value="anio_pasado">Año pasado</option>
                <option value="custom">Personalizado…</option>
              </select>
            </FiltroConTitulo>
            )}
            {puedeFiltroFecha && filtroFecha === "custom" && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: c.textMuted }}>
                  Desde
                  <input
                    ref={fechaDesdeRef}
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                    onClick={(e) => abrirPickerFecha(e.currentTarget)}
                    style={dateInputStyle}
                    title="Fecha desde"
                  />
                </label>
                <span style={{ fontSize: 12, color: c.textMuted }}>—</span>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: c.textMuted }}>
                  Hasta
                  <input
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                    onClick={(e) => abrirPickerFecha(e.currentTarget)}
                    style={dateInputStyle}
                    title="Fecha hasta"
                  />
                </label>
              </div>
            )}
            {puedeFiltroBusqueda && (
            <input
              type="search"
              value={filtroBusqueda}
              onChange={(e) => setFiltroBusqueda(e.target.value)}
              placeholder="Nombre, teléfono, email, operación o ref…"
              aria-label="Buscar por nombre, teléfono, email, operación o referencia"
              style={inputFiltroStyle}
            />
            )}
            {(filtroAgente || filtroAgenteUltimo || filtroAgenteAnterior || filtroAgentePreasignado || filtroFuente || filtroFecha !== "todos" || filtroBusqueda.trim()) && (
              <button onClick={() => {
                setFiltroAgente("");
                setFiltroAgenteUltimo("");
                setFiltroAgenteAnterior("");
                setFiltroAgentePreasignado("");
                setFiltroFuente("");
                setFiltroFecha("todos");
                setFechaDesde("");
                setFechaHasta("");
                setFiltroBusqueda("");
              }}
                style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${c.border}`, background: "transparent", color: "#ef4444", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                ✕ Limpiar
              </button>
            )}
          </div>

          {/* Tabla */}
          <div style={{ flex: 1, overflowY: "auto", overflowX: "auto" }}>
            {itemsFiltrados.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: c.textPlaceholder, fontSize: 13 }}>No hay registros con los filtros aplicados</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${c.border}` }}>
                    <th style={{ padding: "9px 12px", background: isDark ? c.bgPanel : "#f1f5f9", borderRight: `1px solid ${c.border}`, width: 40, textAlign: "center" }}>
                      <input type="checkbox"
                        checked={selectedIds.size === itemsFiltrados.length && itemsFiltrados.length > 0}
                        onChange={e => setSelectedIds(e.target.checked ? new Set(itemsFiltrados.map(i => i.id)) : new Set())}
                        style={{ cursor: "pointer", width: 14, height: 14 }} />
                    </th>
                    {colsVisibles.map(col => (
                      <th key={col.id} style={{ padding: "9px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: c.textMuted, background: isDark ? c.bgPanel : "#f1f5f9", borderRight: `1px solid ${c.border}`, whiteSpace: "nowrap", minWidth: col.width }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {itemsFiltrados.map(item => {
                    const isSelected = selectedIds.has(item.id);
                    const bgSelected = isDark ? "#1e3a5f" : "#eff6ff";
                    return (
                      <tr key={item.id}
                        style={{ borderBottom: `1px solid ${c.border}`, background: isSelected ? bgSelected : c.bgCard, cursor: (modo === "leads" || onClickItem) ? "pointer" : "default" }}
                        onClick={() => { if (modo === "leads" || onClickItem) abrirLead(item); }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = isDark ? (c.bgCardHover || "#1e293b") : (c.accentBlueLight || "#eff6ff"); }}
                        onMouseLeave={e => { e.currentTarget.style.background = isSelected ? bgSelected : c.bgCard; }}>
                        <td style={{ padding: "4px 12px", borderRight: `1px solid ${c.border}`, textAlign: "center", width: 40 }}
                          onClick={e => e.stopPropagation()}>
                          <input type="checkbox"
                            checked={isSelected}
                            onChange={e => {
                              const newSet = new Set(selectedIds);
                              if (e.target.checked) newSet.add(item.id);
                              else newSet.delete(item.id);
                              setSelectedIds(newSet);
                            }}
                            style={{ cursor: "pointer", width: 14, height: 14 }} />
                        </td>
                        {colsVisibles.map(col => (
                          <td key={col.id} style={{ padding: col.id === "fotos" ? "4px 16px" : "9px 16px", fontSize: 12, color: c.textSecondary, borderRight: `1px solid ${c.border}`, whiteSpace: "nowrap", maxWidth: col.width, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {renderCelda(item, col.id)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {modalWA && (
        <ModalWhatsApp
          isOpen={true}
          contact={contactoDesde(modalWA)}
          onClose={() => setModalWA(null)}
          onSend={() => setModalWA(null)}
          idioma={
            modo === "captaciones"
              ? resolverIdiomaPropietarioCaptacion(modalWA)
              : (modalWA.idioma || "ES")
          }
          crmContext={
            modo === "captaciones"
              ? { captacion_id: modalWA.id }
              : { lead_id: modalWA.id }
          }
        />
      )}
      {modalEmail && (
        <ModalEmail
          isOpen={true}
          contact={contactoDesde(modalEmail)}
          onClose={() => setModalEmail(null)}
          onSend={() => setModalEmail(null)}
          idioma={
            modo === "captaciones"
              ? resolverIdiomaPropietarioCaptacion(modalEmail)
              : (modalEmail.idioma || "ES")
          }
          crmContext={
            modo === "captaciones"
              ? { captacion_id: modalEmail.id }
              : { lead_id: modalEmail.id }
          }
        />
      )}
      {modalCitaFrio && citaLead && (
        <CitaDesdeFrioModal
          open={modalCitaFrio}
          lead={citaLead}
          usuario={usuario}
          agentName={usuario?.nombre || ""}
          onClose={() => { setModalCitaFrio(false); setCitaLead(null); }}
          onSuccess={async (result) => {
            setSelectedIds(new Set());
            setModalCitaFrio(false);
            setCitaLead(null);
            await onCitaCreada?.(result, citaLead);
          }}
        />
      )}
      {modalEnvioMasivo && esFrios && modo === "leads" && (
        <ModalEnvioMasivoFrios
          isOpen
          leads={leadsEnvioMasivo}
          onClose={() => setModalEnvioMasivo(false)}
          onComplete={() => {
            setModalEnvioMasivo(false);
            setLeadsEnvioMasivo([]);
            setSelectedIds(new Set());
          }}
        />
      )}
    </>
  );
}

// ============================================================
// EMBUDO HOLOGRÁFICO (Minority Report)
// ============================================================
function EmbudoSVG({ conteos, etapas, modo, items, theme, getFrontendEtapa, onClickItem, onOpenLead, onEliminarItems, onEliminarPermanente }) {
  const [etapaModal, setEtapaModal] = useState(null);
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const total = Object.values(conteos).reduce((a, b) => a + b, 0);
  const W = 190, cx = 95;
  const topW = 176, botW = 14, gap = 6, segH = 52;
  const totalH = etapas.length * segH + (etapas.length - 1) * gap + 40;

  const segs = etapas.map((etapa, i) => {
    const wTop = topW - (topW - botW) * (i / etapas.length);
    const wBot = topW - (topW - botW) * ((i + 1) / etapas.length);
    const y = i * (segH + gap);
    return { etapa, wTop, wBot, y, x1: cx - wTop/2, x2: cx + wTop/2, x3: cx + wBot/2, x4: cx - wBot/2 };
  });

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", background: isDark ? "#000814" : c.bgApp, borderRadius: 12, padding: "14px 10px 12px", border: `1px solid ${isDark ? "rgba(100,200,255,0.15)" : c.border}`, position: "relative", overflow: "hidden", width: "100%" }}>
        {/* Grid holográfico — solo en oscuro */}
        {isDark && <div style={{ position: "absolute", inset: 0, background: "repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(0,180,255,0.012) 3px,rgba(0,180,255,0.012) 4px)", pointerEvents: "none" }} />}

        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", color: isDark ? "#64d8ff" : "#000000", marginBottom: 10, opacity: isDark ? 0.75 : 1, textTransform: "uppercase" }}>— Pipeline —</div>

        <svg width={W} height={totalH} viewBox={`0 0 ${W} ${totalH}`} style={{ overflow: "visible" }}>
          <defs>
            {etapas.map(e => (
              <filter key={e.key} id={`glow-${e.key}`}>
                <feGaussianBlur stdDeviation="2" result="blur"/>
                <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
            ))}
          </defs>

          {segs.map(({ etapa, wTop, wBot, y, x1, x2, x3, x4 }, i) => {
            const count = conteos[etapa.key] || 0;
            const midY  = y + segH / 2;
            const col   = etapa.color;
            return (
              <g key={etapa.key} style={{ cursor: "pointer" }} onClick={() => setEtapaModal(etapa)}
                onMouseEnter={e => { e.currentTarget.querySelector(".seg-fill").style.opacity = "0.35"; }}
                onMouseLeave={e => { e.currentTarget.querySelector(".seg-fill").style.opacity = "0.12"; }}>
                <path className="seg-fill" d={`M${x1},${y} L${x2},${y} L${x3},${y+segH} L${x4},${y+segH} Z`}
                  fill={col} style={{ opacity: isDark ? 0.12 : 0.25, transition: "opacity 0.2s" }}/>
                <path d={`M${x1},${y} L${x2},${y} L${x3},${y+segH} L${x4},${y+segH} Z`}
                  fill="none" stroke={col} strokeWidth={isDark ? 1.2 : 2} opacity="0.9" filter={isDark ? `url(#glow-${etapa.key})` : undefined}/>
                <line x1={x1} y1={y} x2={x4} y2={y+segH} stroke={col} strokeWidth="0.5" opacity={isDark ? 0.35 : 0.6}/>
                <line x1={x2} y1={y} x2={x3} y2={y+segH} stroke={col} strokeWidth="0.5" opacity={isDark ? 0.35 : 0.6}/>
                <line x1={x1} y1={y} x2={x2} y2={y} stroke={col} strokeWidth="1.8" opacity={isDark ? 0.65 : 1}/>
                <text x={cx} y={midY - 7} textAnchor="middle"
                  style={{ fontSize: 10, fontWeight: 700, fill: isDark ? col : "#000000", letterSpacing: "0.16em", opacity: 0.9 }}>
                  {etapa.label.toUpperCase()}
                </text>
                <text x={cx} y={midY + 12} textAnchor="middle"
                  style={{ fontSize: 21, fontWeight: 900, fill: isDark ? col : "#000000", fontFamily: "monospace", opacity: 0.9 }}>
                  {count}
                </text>
                <text x={x1 - 4} y={midY + 3} textAnchor="end"
                  style={{ fontSize: 9, fill: isDark ? "rgba(100,216,255,0.3)" : "rgba(0,0,0,0.25)", fontFamily: "monospace" }}>
                  {String(i + 1).padStart(2, "0")}
                </text>
                {i < segs.length - 1 && (
                  <>
                    <line x1={cx} y1={y+segH+1} x2={cx} y2={y+segH+gap-1} stroke={col} strokeWidth="1" strokeDasharray="2,2" opacity="0.5"/>
                    <circle cx={cx} cy={y+segH+gap/2} r="1.5" fill={col} opacity="0.8"/>
                  </>
                )}
              </g>
            );
          })}
          <polygon points={`${cx-4},${totalH-38} ${cx},${totalH-28} ${cx+4},${totalH-38}`} fill="#3b82f6" opacity="0.7"/>
        </svg>

        <div style={{ borderTop: `1px solid ${isDark ? "rgba(100,216,255,0.12)" : c.border}`, paddingTop: 8, textAlign: "center", width: "100%", marginTop: 4 }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: isDark ? "#64d8ff" : "#000000", lineHeight: 1, fontFamily: "monospace" }}>{total}</div>
          <div style={{ fontSize: 11, color: isDark ? "rgba(100,216,255,0.5)" : "#000000", marginTop: 2, letterSpacing: "0.18em", textTransform: "uppercase" }}>activas · en curso</div>
        </div>

        {isDark && <div style={{ position: "absolute", top: 10, right: 10, width: 6, height: 6, borderRadius: "50%", background: "#64d8ff", opacity: 0.7 }} />}
        {isDark && <div style={{ position: "absolute", top: 10, left: 10, fontSize: 8, color: "rgba(100,216,255,0.3)", fontFamily: "monospace", letterSpacing: "0.1em" }}>SYS·ON</div>}
      </div>

      {etapaModal && (
        <ModalTablaEmbudo
          etapa={etapaModal}
          items={items.filter((i) => {
            // Misma regla que itemsPorEtapaBase: propiedades CRM (con Ref.) solo en «Captaciones».
            // Si no, etapa_pipeline residual (p. ej. revisar_cap) infla Para Revisar en tabla.
            if (modo === "captaciones" && i.origen === "propiedad") {
              return etapaModal.key === "captaciones";
            }
            return getFrontendEtapa(i.etapa) === etapaModal.key;
          })}
          modo={modo}
          theme={theme}
          onClose={() => setEtapaModal(null)}
          onClickItem={onClickItem}
          onOpenLead={modo === "leads" ? onClickItem : undefined}
          onEliminar={onEliminarItems}
          onEliminarPermanente={modo === "leads" ? onEliminarPermanente : undefined}
        />
      )}
    </>
  );
}

// ============================================================
// TARJETA LEAD
// ============================================================
const TarjetaLead = forwardRef(function TarjetaLead({ item, etapa, theme, onClick, onDragStart, motionGhost, onAbrirVistaPreviaRef }, ref) {
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const [hover, setHover] = useState(false);
  const pointer = usePipelineCardClickDrag(
    motionGhost ? undefined : onClick,
    motionGhost ? undefined : onDragStart,
  );

  const { fechaStr, horaStr } = formatFechaHoraPipeline(fechaReferenciaPipeline(item));

  const operacionColor = item.operacion?.toLowerCase() === "alquiler"
    ? c.accentPink
    : item.operacion?.toLowerCase() === "traspaso"
    ? c.accentOrange || "#f97316"
    : c.accentGreen;

  const bordeLateral = hover ? etapa.color : c.border;

  const exTarjeta = (typeof item.datos_extras === "object" && item.datos_extras !== null)
    ? item.datos_extras
    : (() => { try { return JSON.parse(item.datos_extras || "{}"); } catch { return {}; } })();
  const relCliente = exTarjeta?.relacion_cliente;
  const esRepetido = relCliente === "cliente_activo" || relCliente === "cliente_frios";
  const etiquetaRepetido = relCliente === "cliente_frios"
    ? "CLIENTE ASIGNADO DESDE FRÍOS. REVISA SU FICHA"
    : "CLIENTE EXISTENTE. REVISA SU FICHA";
  const fuenteLabel = fuenteVisibleEnLead(item.fuente);
  const agenteLabel = agenteResponsableLabel(item);
  const textoTarjeta = isDark ? "#ffffff" : c.textPrimary;
  const amarilloTarjeta = "#eab308";
  const verdeTarea = c.accentGreen || "#22c55e";
  const naranjaRef = "#f97316";
  // El código que viaja del backend es el interno (T2A, TC…). En la tarjeta se
  // muestra el mismo que en Tareas y en la ficha: T2A · Cualificar se ve como el
  // contacto que la abrió (T1c si el lead respondió al WhatsApp).
  const tareaCodigoVisible = item.tarea_codigo
    ? getTaskDisplayCode({
        metadata: item.tarea_codigo_meta || { task_code: item.tarea_codigo },
        lead_id: item.id,
      }) || item.tarea_codigo
    : null;

  return (
    <div
      ref={ref}
      draggable={!motionGhost}
      onMouseDown={motionGhost ? undefined : pointer.onMouseDown}
      onDragStart={motionGhost ? undefined : pointer.onDragStart}
      onDragEnd={motionGhost ? undefined : pointer.onDragEnd}
      onClick={motionGhost ? undefined : pointer.onClick}
      onMouseEnter={() => !motionGhost && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: c.bgCard,
        borderTop: `3px solid ${etapa.color}`,
        borderLeft: `1px solid ${bordeLateral}`,
        borderRight: `1px solid ${bordeLateral}`,
        borderBottom: `1px solid ${bordeLateral}`,
        borderRadius: 10, padding: "10px 12px", cursor: motionGhost ? "default" : "pointer",
        transition: motionGhost ? "none" : "box-shadow 0.2s ease, transform 0.2s ease",
        boxShadow: hover
          ? `0 4px 16px ${isDark ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.1)"}, 0 0 0 1px ${etapa.color}40`
          : `0 1px 3px ${isDark ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.06)"}`,
        transform: hover && !motionGhost ? "translateY(-1px)" : "none",
      }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: amarilloTarjeta, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {item.nombre} {item.apellido || ""}
        </span>
        {item.operacion && (
          <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", color: operacionColor }}>{item.operacion}</span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 3 }}>
        <span style={{ fontSize: 14, color: textoTarjeta }}>{item.telefono || "—"}</span>
        {item.propiedad_ref && (
          <span
            role="link"
            tabIndex={0}
            data-pipeline-no-click
            title="Ver vista previa de la propiedad"
            onClick={(e) => onAbrirVistaPreviaRef?.(e, item.propiedad_ref)}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && onAbrirVistaPreviaRef) {
                onAbrirVistaPreviaRef(e, item.propiedad_ref);
              }
            }}
            style={{
              fontSize: 13,
              fontWeight: 700,
              padding: "1px 6px",
              borderRadius: 4,
              background: naranjaRef,
              color: "#0f172a",
              border: `1px solid ${naranjaRef}`,
              cursor: "pointer",
            }}
          >
            Ref. {item.propiedad_ref}
          </span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, marginTop: 2 }}>
        <span style={{ fontSize: 14, color: textoTarjeta, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{item.email || "—"}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, marginTop: 4 }}>
        <span style={{ fontSize: 13, color: textoTarjeta }}>
          Entrada: {fechaStr}{horaStr ? ` ${horaStr}` : ""}
        </span>
        {fuenteLabel && (
          <span style={{ fontSize: 11, fontWeight: 700, padding: "1px 6px", borderRadius: 5, ...procedenciaChipStyle(fuenteLabel) }}>
            {fuenteLabel}
          </span>
        )}
      </div>
      {(tareaCodigoVisible || agenteLabel) && (
        <div style={{
          marginTop: 4, display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 8, fontSize: 13, minHeight: 18,
        }}>
          {tareaCodigoVisible ? (
            <span style={{
              fontWeight: 700, color: verdeTarea, flex: 1, minWidth: 0,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {tareaCodigoVisible}
              {item.tarea_vencimiento && (() => {
                const vto = new Date(item.tarea_vencimiento);
                const vencida = !isNaN(vto.getTime()) && vto.getTime() < Date.now();
                const { fechaStr: vFecha, horaStr: vHora } = formatFechaHoraPipeline(item.tarea_vencimiento);
                return (
                  <span style={{ color: vencida ? "#ef4444" : verdeTarea }}>
                    {" · "}Vto.: {vFecha}{vHora ? ` ${vHora}` : ""}
                  </span>
                );
              })()}
            </span>
          ) : (
            <span style={{ flex: 1 }} />
          )}
          {agenteLabel && (
            <span style={{
              fontWeight: 600, color: amarilloTarjeta, flexShrink: 0, whiteSpace: "nowrap",
            }}>
              {agenteLabel}
            </span>
          )}
        </div>
      )}
      {esRepetido && (
        <div style={{
          margin: "8px -12px -10px",
          padding: "5px 12px",
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: "0.03em",
          textAlign: "center",
          color: "#ffffff",
          background: "#dc2626",
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
        }}>
          {etiquetaRepetido}
        </div>
      )}
    </div>
  );
});
TarjetaLead.displayName = "TarjetaLead";

// ============================================================
// TARJETA CAPTACION
// ============================================================
function TarjetaCaptacion({ item, etapa, theme, onClick, onDragStart, canDrag = true }) {
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const [hover, setHover] = useState(false);
  const [fechaHover, setFechaHover] = useState(false);
  const [tooltipPos, setTooltipPos] = useState(null);
  const fechaRef = useRef(null);
  const pointer = usePipelineCardClickDrag(onClick, canDrag ? onDragStart : undefined);

  const { fechaStr, horaStr } = formatFechaHoraPipeline(fechaReferenciaPipeline(item));
  const lineasEtapas = lineasEtapasEntradaCaptacion(item);

  const operacionColor = item.operacion?.toLowerCase() === "alquiler"
    ? c.accentPink
    : item.operacion?.toLowerCase() === "traspaso"
    ? c.accentOrange || "#f97316"
    : c.accentGreen;

  const extrasItem =
    typeof item.datos_extras === "object" && item.datos_extras !== null
      ? item.datos_extras
      : (() => { try { return JSON.parse(item.datos_extras || "{}"); } catch { return {}; } })();

  const tipoInmuebleTxt = tipoInmuebleLegible(item, extrasItem);
  const refMostrar = refPipelineCard(item, extrasItem);
  const precioNum = precioPipelineCard(item, extrasItem);
  const precioTxt = precioNum != null
    ? `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(precioNum)} €`
    : null;

  const agenteLabel = agenteResponsableLabel(item);

  const bordeLateralCap = hover ? etapa.color : c.border;
  const detailText = isDark ? "#f1f5f9" : c.textPrimary;
  const amarilloTarjeta = "#eab308";
  const chipFuente = fuenteVisibleEnLead(item.fuente);
  const chipFuenteStyle = chipFuente ? procedenciaChipStyle(chipFuente) : null;
  const etiquetaMultimedia = etiquetaMultimediaPipeline(item);
  const esPrueba = esCaptacionPrueba(item, extrasItem);

  const fotosIdentificacion = Array.isArray(item.fotos)
    ? item.fotos
    : (item.fotos ? (() => { try { return JSON.parse(item.fotos); } catch { return []; } })() : []);
  const fotosReportaje = Array.isArray(item.fotos_reportaje)
    ? item.fotos_reportaje
    : (item.fotos_reportaje ? (() => { try { return JSON.parse(item.fotos_reportaje); } catch { return []; } })() : []);
  const portadaRaw = fotosReportaje.length > 0 ? fotosReportaje[0] : (fotosIdentificacion.length > 0 ? fotosIdentificacion[0] : null);
  const portada = portadaRaw ? resolveCaptacionMediaUrl(portadaRaw) : null;
  // El listado solo manda 1 URL de portada (slim); el total real viene en fotos_reportaje_count.
  const totalFotos = Math.max(
    Number(item.fotos_reportaje_count) || 0,
    fotosReportaje.length + fotosIdentificacion.length,
  );

  const direccion = direccionPipelineCard(item, extrasItem);

  const actualizarTooltipPos = () => {
    const el = fechaRef.current;
    if (!el || !lineasEtapas.length) {
      setTooltipPos(null);
      return;
    }
    const r = el.getBoundingClientRect();
    const preferArriba = r.top > 160;
    const ancho = 300;
    setTooltipPos({
      left: Math.min(Math.max(8, r.left), window.innerWidth - (ancho + 12)),
      top: preferArriba ? r.top - 8 : r.bottom + 8,
      place: preferArriba ? "above" : "below",
      width: ancho,
    });
  };

  return (
    <div
      draggable={canDrag}
      onMouseDown={pointer.onMouseDown}
      onDragStart={canDrag ? pointer.onDragStart : undefined}
      onDragEnd={canDrag ? pointer.onDragEnd : undefined}
      onClick={pointer.onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: c.bgCard,
        borderTop: `3px solid ${etapa.color}`,
        borderLeft: `1px solid ${bordeLateralCap}`,
        borderRight: `1px solid ${bordeLateralCap}`,
        borderBottom: `1px solid ${bordeLateralCap}`,
        borderRadius: 10,
        overflow: "hidden",
        cursor: canDrag ? "pointer" : "default",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        boxShadow: hover
          ? `0 4px 16px ${isDark ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.1)"}, 0 0 0 1px ${etapa.color}40`
          : `0 1px 3px ${isDark ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.06)"}`,
        transform: hover ? "translateY(-1px)" : "none",
      }}
    >
      {fechaHover && tooltipPos && lineasEtapas.length > 0 && createPortal(
        <div
          role="tooltip"
          style={{
            position: "fixed",
            left: tooltipPos.left,
            top: tooltipPos.top,
            transform: tooltipPos.place === "above" ? "translateY(-100%)" : "none",
            zIndex: 9999,
            width: tooltipPos.width,
            minWidth: tooltipPos.width,
            padding: "8px 12px",
            borderRadius: 8,
            background: isDark ? "rgba(15,23,42,0.96)" : "rgba(255,255,255,0.98)",
            border: `1px solid ${etapa.color}66`,
            boxShadow: isDark
              ? "0 8px 24px rgba(0,0,0,0.45)"
              : "0 8px 24px rgba(0,0,0,0.12)",
            pointerEvents: "none",
            fontFamily: "inherit",
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: c.textMuted, marginBottom: 6 }}>
            Entrada por etapa
          </div>
          {lineasEtapas.map((ln) => (
            <div key={ln.key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3, whiteSpace: "nowrap" }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: ln.color, flexShrink: 0 }} />
              <span style={{ flex: "1 1 auto", fontSize: 11, fontWeight: 600, color: detailText }}>
                {ln.label}
              </span>
              <span style={{ fontSize: 11, color: isDark ? "#cbd5e1" : c.textSecondary, flexShrink: 0 }}>
                {ln.texto}
              </span>
            </div>
          ))}
        </div>,
        document.body,
      )}
      {/* Cuerpo: info izquierda + foto 16:9 arriba derecha */}
      <div style={{ display: "flex", alignItems: "stretch" }}>
        {/* Columna de texto — se estira hasta la altura de la foto y reparte líneas */}
        <div style={{ flex: 1, minWidth: 0, padding: "8px 6px 8px 10px", display: "flex", flexDirection: "column", justifyContent: "space-evenly" }}>
          {/* Línea 1 — Ref (etapa captaciones) o Nombre propietario (etapas 1-4) */}
          <div style={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
            {etapa.key === "captaciones" ? (
              refMostrar && (
                <span style={{ fontSize: 13, fontWeight: 800, color: isDark ? "#60a5fa" : c.accentBlue, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: "-0.01em" }}>
                  Ref. {refMostrar}
                </span>
              )
            ) : (
              (item.nombre || item.apellido) && (
                <span style={{ fontSize: 13, fontWeight: 700, color: amarilloTarjeta, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {[item.nombre, item.apellido].filter(Boolean).join(" ")}
                </span>
              )
            )}
          </div>
          {/* Línea 2 — Teléfono */}
          <div style={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
            {item.telefono && (
              <span style={{ fontSize: 12, color: detailText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.telefono}
              </span>
            )}
          </div>
          {/* Línea 3 — Tipo */}
          <div style={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
            {tipoInmuebleTxt && (
              <span style={{ fontSize: 12, color: detailText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {tipoInmuebleTxt}
              </span>
            )}
          </div>
          {/* Línea 4 — Precio */}
          <div style={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
            {precioTxt && (
              <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.02em", color: operacionColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {precioTxt}
              </span>
            )}
          </div>
          {/* Línea 5 — Agente / Ana */}
          <div style={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
            {agenteLabel && (
              <span style={{
                fontSize: 12,
                fontWeight: 600,
                color: amarilloTarjeta,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {agenteLabel}
              </span>
            )}
          </div>
        </div>

        {/* Foto 16:9, esquina superior derecha, sin barras negras */}
        <div style={{
          width: "65%",
          aspectRatio: "16/9",
          position: "relative",
          overflow: "hidden",
          flexShrink: 0,
          background: c.bgCard,
          borderRadius: "0 9px 0 0",
        }}>
          {portada ? (
            <img
              src={portada}
              alt=""
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }}
            />
          ) : (
            <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.2 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c.textMuted} strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </div>
          )}
          {item.operacion && (
            <span style={{
              position: "absolute", top: 5, right: 5, zIndex: 2,
              fontSize: 11, fontWeight: 800, padding: "2px 6px", borderRadius: 4,
              color: operacionColor,
              background: "rgba(0,0,0,0.65)",
              backdropFilter: "blur(4px)",
              border: `1px solid ${operacionColor}40`,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}>
              {item.operacion}
            </span>
          )}
        </div>
      </div>

      {/* Línea dirección — ancho completo sin procedencia */}
      <div style={{ padding: "3px 10px 4px", display: "flex", alignItems: "flex-start", gap: 6, minHeight: 20, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: detailText, flex: "1 1 100%", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {direccion || ""}
        </span>
        {esPrueba && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 5, whiteSpace: "nowrap", flexShrink: 0,
            background: "rgba(245,158,11,0.35)",
            color: isDark ? "#ffffff" : "#000000",
            border: "1px solid rgba(245,158,11,0.55)",
          }}>
            🧪 Prueba
          </span>
        )}
        {etiquetaMultimedia && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 5,
            whiteSpace: "normal", lineHeight: 1.25, flex: "1 1 auto", minWidth: 0, maxWidth: "100%",
            background: (etiquetaMultimedia === "Procesándose"
              || etiquetaMultimedia === "Creando carpeta"
              || etiquetaMultimedia === "Fusionando carpetas")
              ? "rgba(139,92,246,0.18)"
              : /falta preparar|no lleg/i.test(String(etiquetaMultimedia))
                ? "rgba(239,68,68,0.14)"
              : "rgba(245,158,11,0.18)",
            color: (etiquetaMultimedia === "Procesándose"
              || etiquetaMultimedia === "Creando carpeta"
              || etiquetaMultimedia === "Fusionando carpetas")
              ? "#a78bfa"
              : /falta preparar|no lleg/i.test(String(etiquetaMultimedia))
                ? "#dc2626"
              : "#f59e0b",
          }}>
            {etiquetaMultimedia}
          </span>
        )}
      </div>

      {/* Footer: fecha · hora · procedencia · nº fotos */}
      <div style={{ borderTop: `1px solid ${c.border}`, padding: "5px 10px 6px", display: "flex", alignItems: "center", gap: 6 }}>
        <span
          ref={fechaRef}
          onMouseEnter={(e) => {
            e.stopPropagation();
            setFechaHover(true);
            actualizarTooltipPos();
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            setFechaHover(false);
            setTooltipPos(null);
          }}
          style={{
            fontSize: 11,
            color: detailText,
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
            flexShrink: 0,
            cursor: lineasEtapas.length ? "help" : "inherit",
            borderBottom: lineasEtapas.length
              ? `1px dotted ${fechaHover ? etapa.color : (isDark ? "rgba(148,163,184,0.55)" : "rgba(100,116,139,0.45)")}`
              : "none",
            paddingBottom: 1,
          }}
        >
          {fechaStr}{horaStr ? ` · ${horaStr}` : ""}
        </span>
        {chipFuente && chipFuenteStyle && (
          <span style={{
            fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 4, whiteSpace: "nowrap", flexShrink: 1,
            minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
            ...chipFuenteStyle, border: "none",
          }}>
            {chipFuente}
          </span>
        )}
        <span style={{ flex: 1, minWidth: 4 }} />
        {totalFotos > 0 && (
          <span style={{ fontSize: 11, color: detailText, letterSpacing: "0.02em", whiteSpace: "nowrap", flexShrink: 0 }}>
            {totalFotos} foto{totalFotos !== 1 ? "s" : ""}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
export default function Pipeline() {
  const navigate = useNavigate();
  const location = useLocation();
  const pipelineReturnTo = `${location.pathname}${location.search}`;
  const { theme } = useTheme();
  const { usuario } = useAuth();
  const { puede } = usePermiso();
  const puedeCrearPipeline = puede("pipeline", "alta");
  const c = theme.colors;
  const isDark = theme.name === "dark";

  const [searchParams, setSearchParams] = useSearchParams();
  const openLeadId = searchParams.get("openLead");
  const openFriosParam = searchParams.get("openFrios");
  const [modo, setModo] = useState(searchParams.get("modo") === "captaciones" ? "captaciones" : "leads");

  // Botón "Procesar email" tiene su propio nodo de permisos (independiente de
  // "Nuevo lead"/"Nueva captación", que solo dependen de pipeline.alta).
  const nodeIdProcesarEmail = modo === "captaciones" ? "pl_capt_procesar_email" : "pl_leads_procesar_email";
  const puedeProcesarEmail = puede(nodeIdProcesarEmail, "ver");
  const nodeIdNuevoRegistro = modo === "captaciones" ? "pl_capt_nueva" : "pl_leads_nuevo";
  const puedeNuevoRegistro = puede(nodeIdNuevoRegistro, "ver");

  // Filtros globales del pipeline (modal «Filtros»): fechas para todos; usuarios solo
  // Admin/Director/Secretaría (filtra por agente responsable en Leads y Captaciones).
  const puedeFiltrarPorUsuariosPipeline = ["admin", "director", "secretaria"].includes(
    String(usuario?.rol || "").toLowerCase()
  );
  const [usuariosPipelineLista, setUsuariosPipelineLista] = useState([]);
  const [usuariosPipelineFiltro, setUsuariosPipelineFiltro] = useState([]);
  const [cargandoUsuariosPipeline, setCargandoUsuariosPipeline] = useState(false);
  const [errorUsuariosPipeline, setErrorUsuariosPipeline] = useState("");
  const [mostrarModalFiltrosPipeline, setMostrarModalFiltrosPipeline] = useState(false);
  const [filtroFechaPipeline, setFiltroFechaPipeline] = useState("todos");
  const [fechaDesdePipeline, setFechaDesdePipeline] = useState("");
  const [fechaHastaPipeline, setFechaHastaPipeline] = useState("");
  const [filtroTextoPipeline, setFiltroTextoPipeline] = useState("");
  const fechaDesdePipelineRef = useRef(null);

  const etapas      = modo === "leads" ? ETAPAS_LEADS : ETAPAS_CAPTACIONES;
  const endpointBase = modo === "leads" ? "/leads" : "/captaciones";

  const getFrontendEtapa = (etapa) => {
    if (modo === "leads") return etapa;
    return MAPA_ETAPAS_CAPTACIONES_BACK_TO_FRONT[etapa] || etapa;
  };

  const getBackendEtapa = (etapa) => {
    if (modo === "leads") return etapa;
    return MAPA_ETAPAS_CAPTACIONES_FRONT_TO_BACK[etapa] || etapa;
  };

  const [items, setItems]                     = useState([]);
  const [cargando, setCargando]               = useState(true);
  const [dragging, setDragging]               = useState(null);
  const [dragOver, setDragOver]               = useState(null);
  const [mostrarForm, setMostrarForm]         = useState(false);
  const [leadEditando, setLeadEditando]       = useState(null);
  const [captacionEditando, setCaptacionEditando]         = useState(null);
  const [captacionRevisando, setCaptacionRevisando]       = useState(null);
  const [fichaOverlayCaptacion, setFichaOverlayCaptacion] = useState(null);
  const fichaOverlayCloseRef = useRef(null);
  const [captacionCompletando, setCaptacionCompletando]   = useState(null); // Visita Concertada → agente
  const [borradoresEntradaDirecta, setBorradoresEntradaDirecta] = useState([]);
  const [mostrarBorradoresCaptacion, setMostrarBorradoresCaptacion] = useState(false);
  const [borradorSeleccionadoId, setBorradorSeleccionadoId] = useState(null);
  const [eliminandoBorrador, setEliminandoBorrador] = useState(false);
  const [retrocesoMotivoPendiente, setRetrocesoMotivoPendiente] = useState(null);
  const [camposFaltantesAgente, setCamposFaltantesAgente] = useState([]);
  // Modal de transición (arrastrar entre etapas con datos requeridos)
  const [transicionPendiente, setTransicionPendiente]     = useState(null); // { item, etapaDestino, tipo }
  const [mostrarEmail, setMostrarEmail]       = useState(false);
  const [mostrarEliminados, setMostrarEliminados] = useState(false);
  const [mostrarFrios, setMostrarFrios]       = useState(false);
  const [eliminados, setEliminados]           = useState([]);
  const [frios, setFrios]                     = useState([]);
  const [conteoFrios, setConteoFrios]         = useState(null);
  const [conteoEliminados, setConteoEliminados] = useState(null);
  const [vistaPreviaPropId, setVistaPreviaPropId] = useState(null);
  /** Pipeline captaciones: columna final «Captaciones» (persistida en localStorage) */
  const [captacionesColExpandida, setCaptacionesColExpandida] = useState(() =>
    readPipelineColExpandida(LS_PIPELINE_CAPTACIONES_COL, false)
  );
  /** Pipeline leads: columna final «Ventas» (persistida en localStorage) */
  const [ventasColExpandida, setVentasColExpandida] = useState(() =>
    readPipelineColExpandida(LS_PIPELINE_VENTAS_COL, false)
  );

  const toggleCaptacionesColExpandida = useCallback((next) => {
    setCaptacionesColExpandida(next);
    writePipelineColExpandida(LS_PIPELINE_CAPTACIONES_COL, next);
  }, []);

  const toggleVentasColExpandida = useCallback((next) => {
    setVentasColExpandida(next);
    writePipelineColExpandida(LS_PIPELINE_VENTAS_COL, next);
  }, []);

  const [textoEmail, setTextoEmail]           = useState("");
  const [procesandoEmail, setProcesandoEmail] = useState(false);
  const [resultadoEmail, setResultadoEmail]   = useState(null);

  const leadMotionRef = useRef(null);
  const colHeaderRefs = useRef({});
  const colBodyRefs = useRef({});
  const leadCardRefs = useRef({});
  const prevAllItemIdsRef = useRef(null);
  const skyFallQueuedRef = useRef(new Set());
  const prevLeadRealEtapaRef = useRef(new Map());
  const leadEtapaPrevReadyRef = useRef(false);
  const autoArcInFlightRef = useRef(new Set());
  const itemsRef = useRef([]);
  itemsRef.current = items;
  const cargarAbortRef = useRef(null);
  const pageAbortRef = useRef(null);
  const endpointCargadoRef = useRef(null);

  const [leadSkyPending, setLeadSkyPending] = useState(() => new Set());
  const [leadArcHide, setLeadArcHide] = useState(null);
  const [leadReceivingCol, setLeadReceivingCol] = useState(null);
  /** Mientras Ana (u otro) cambia etapa en servidor, forzamos columna visual hasta terminar el arco */
  const [leadDisplayEtapaOverride, setLeadDisplayEtapaOverride] = useState({});

  useReloadOnRouteEnter(() => {
    cargar();
  }, [modo, endpointBase]);

  useEffect(() => {
    const ac = new AbortController();
    pageAbortRef.current = ac;
    return () => {
      ac.abort();
      cargarAbortRef.current?.abort();
    };
  }, []);

  // Lista de usuarios para el filtro: mismo endpoint que Agenda/Tareas.
  // ORIGEN: un GET al montar + catch vacío dejaba «Cargando usuarios…» eterno
  // si fallaba o el parse no veía el array. Se recarga al abrir el modal.
  const cargarUsuariosPipeline = useCallback(async () => {
    if (!puedeFiltrarPorUsuariosPipeline) return;
    setCargandoUsuariosPipeline(true);
    setErrorUsuariosPipeline("");
    try {
      const res = await api.get("/agenda/usuarios");
      let lista = normalizarUsuariosFiltroPipeline(parseListaUsuariosRespuesta(res));
      if (!lista.length) {
        const resAg = await api.get("/agentes");
        const agentes = parseListaUsuariosRespuesta(resAg);
        lista = normalizarUsuariosFiltroPipeline(
          agentes.map((a) => ({
            id: a.id,
            nombre: a.nombre,
            rol: a.es_ia ? "ia" : "agente",
            agente_id: a.id,
            activo: a.activo,
          }))
        );
      }
      setUsuariosPipelineLista(lista);
      if (!lista.length) setErrorUsuariosPipeline("No hay usuarios para mostrar.");
    } catch (e) {
      console.warn("[pipeline] usuarios filtro:", e?.message || e);
      try {
        const resAg = await api.get("/agentes");
        const agentes = parseListaUsuariosRespuesta(resAg);
        const lista = normalizarUsuariosFiltroPipeline(
          agentes.map((a) => ({
            id: a.id,
            nombre: a.nombre,
            rol: a.es_ia ? "ia" : "agente",
            agente_id: a.id,
            activo: a.activo,
          }))
        );
        setUsuariosPipelineLista(lista);
        if (!lista.length) setErrorUsuariosPipeline("No se pudieron cargar los usuarios.");
        else setErrorUsuariosPipeline("");
      } catch (e2) {
        console.warn("[pipeline] usuarios filtro fallback agentes:", e2?.message || e2);
        setErrorUsuariosPipeline("No se pudieron cargar los usuarios.");
      }
    } finally {
      setCargandoUsuariosPipeline(false);
    }
  }, [puedeFiltrarPorUsuariosPipeline]);

  useEffect(() => {
    cargarUsuariosPipeline();
  }, [cargarUsuariosPipeline]);

  useEffect(() => {
    const pollMs =
      modo === "leads"
        ? Math.min(45000, Math.max(10000, Math.round(28000 / ANA_PIPELINE_MOTION_SPEED)))
        : 10000; // captaciones: espejo rápido con APP
    const ac = new AbortController();
    const refresh = () => {
      api.get(endpointBase, { signal: ac.signal }).then((res) => setItems(res.data.data)).catch(() => {});
    };
    const intervalo = setInterval(refresh, pollMs);
    const onForeground = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      refresh();
    };
    // MarketPlace → Pipeline: refresco inmediato al enviar (no esperar poll 10s)
    const onMpEnviado = () => {
      if (modo === "captaciones") refresh();
    };
    document.addEventListener("visibilitychange", onForeground);
    window.addEventListener("focus", onForeground);
    window.addEventListener("vc-mp-enviado-pipeline", onMpEnviado);
    return () => {
      ac.abort();
      window.removeEventListener("vc-mp-enviado-pipeline", onMpEnviado);
      clearInterval(intervalo);
      document.removeEventListener("visibilitychange", onForeground);
      window.removeEventListener("focus", onForeground);
    };
  }, [modo, endpointBase]);

  useEffect(() => {
    if (!openLeadId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/leads/${openLeadId}`);
        if (cancelled) return;
        const data = res.data?.data;
        if (data) {
          setModo("leads");
          setLeadEditando(data);
        }
      } catch (e) {
        console.error("Error abriendo lead desde URL:", e);
      } finally {
        if (!cancelled) {
          const next = new URLSearchParams(searchParams);
          next.delete("openLead");
          setSearchParams(next, { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [openLeadId]);

  /** Volver desde ficha cliente con ?openFrios=1 → reabre el listado Fríos. */
  useEffect(() => {
    if (openFriosParam !== "1" && openFriosParam !== "true") return;
    setModo("leads");
    setMostrarFrios(true);
    void cargarFrios();
    const next = new URLSearchParams(searchParams);
    next.delete("openFrios");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al llegar con el flag
  }, [openFriosParam]);

  useEffect(() => {
    if (modo !== "leads") {
      // No borrar prevAllItemIdsRef: al volver a Leads evita tratar todos como "nuevos" (caída desde el cielo).
      skyFallQueuedRef.current = new Set();
      prevLeadRealEtapaRef.current = new Map();
      leadEtapaPrevReadyRef.current = false;
      autoArcInFlightRef.current = new Set();
      setLeadSkyPending(new Set());
      setLeadArcHide(null);
      setLeadReceivingCol(null);
      setLeadDisplayEtapaOverride({});
    }
  }, [modo]);

  useEffect(() => {
    if (modo !== "leads" || cargando) return;
    if (!itemsLookLikeLeadPipeline(items)) return;
    const currentIds = new Set(items.map((it) => it.id));
    const prev = prevAllItemIdsRef.current;
    if (!prev) {
      // Nunca fijar snapshot vacío: si items aún no llegaron, el siguiente llenado dispararía caídas para todos.
      if (items.length === 0) return;
      prevAllItemIdsRef.current = currentIds;
      return;
    }
    const nuevosEnLeads = items.filter((it) => !prev.has(it.id) && it.etapa === "leads");
    for (const item of nuevosEnLeads) {
      if (skyFallQueuedRef.current.has(item.id)) continue;
      skyFallQueuedRef.current.add(item.id);
      setLeadSkyPending((p) => new Set(p).add(item.id));
      leadMotionRef.current
        ?.enqueueSkyFall({
          item,
          onLand: () => {
            setLeadSkyPending((p) => {
              const n = new Set(p);
              n.delete(item.id);
              return n;
            });
            skyFallQueuedRef.current.delete(item.id);
          },
        })
        .catch(() => {
          skyFallQueuedRef.current.delete(item.id);
          setLeadSkyPending((p) => {
            const n = new Set(p);
            n.delete(item.id);
            return n;
          });
        });
    }
    prevAllItemIdsRef.current = currentIds;
  }, [items, modo, cargando]);

  useEffect(() => {
    if (modo !== "leads" || cargando) return;
    if (!itemsLookLikeLeadPipeline(items)) return;
    const prev = prevLeadRealEtapaRef.current;
    if (!leadEtapaPrevReadyRef.current) {
      items.forEach((it) => prev.set(it.id, it.etapa));
      leadEtapaPrevReadyRef.current = true;
      return;
    }
    const toAnimate = [];
    for (const item of items) {
      const old = prev.get(item.id);
      const neu = item.etapa;
      if (old === undefined) {
        prev.set(item.id, neu);
        continue;
      }
      if (old === neu) continue;
      if (!ETAPAS_LEADS_KEYS.has(old) || !ETAPAS_LEADS_KEYS.has(neu)) {
        prev.set(item.id, neu);
        continue;
      }
      if (autoArcInFlightRef.current.has(item.id)) {
        prev.set(item.id, neu);
        continue;
      }
      if (leadSkyPending.has(item.id)) {
        prev.set(item.id, neu);
        continue;
      }
      toAnimate.push({ item, fromKey: old, toKey: neu });
      prev.set(item.id, neu);
    }
    if (toAnimate.length === 0) return;
    flushSync(() => {
      setLeadDisplayEtapaOverride((o) => {
        const n = { ...o };
        for (const { item, fromKey } of toAnimate) n[item.id] = fromKey;
        return n;
      });
    });
    for (const { item, fromKey, toKey } of toAnimate) {
      autoArcInFlightRef.current.add(item.id);
      leadMotionRef.current
        ?.enqueueArc({
          item,
          fromKey,
          toKey,
          onBeforeStart: () => setLeadArcHide({ id: item.id, from: fromKey }),
          onLand: async () => {
            setLeadDisplayEtapaOverride((o) => {
              const x = { ...o };
              delete x[item.id];
              return x;
            });
            setLeadArcHide(null);
            autoArcInFlightRef.current.delete(item.id);
          },
        })
        .catch(() => {
          autoArcInFlightRef.current.delete(item.id);
          setLeadDisplayEtapaOverride((o) => {
            const x = { ...o };
            delete x[item.id];
            return x;
          });
          setLeadArcHide(null);
        });
    }
  }, [items, modo, cargando, leadSkyPending]);

  const handleNuevaCaptacion = () => {
    if (borradoresEntradaDirecta.length > 0) {
      setBorradorSeleccionadoId(null);
      setMostrarBorradoresCaptacion(true);
      return;
    }
    try {
      sessionStorage.removeItem("vitalcasa_ficha_captacion_id");
    } catch {
      /* ignore */
    }
    navigate("/propiedades/nueva", {
      state: {
        pendingNuevaCaptacion: true,
        entradaDirectaNueva: true,
        returnTo: pipelineReturnTo,
      },
    });
  };

  const continuarBorradorEntradaDirecta = (cap) => {
    if (!cap?.id) return;
    setMostrarBorradoresCaptacion(false);
    setBorradorSeleccionadoId(null);
    try {
      sessionStorage.setItem("vitalcasa_ficha_captacion_id", String(cap.id));
    } catch {
      /* ignore */
    }
    navigate("/propiedades/nueva", {
      state: {
        pendingNuevaCaptacion: true,
        fromCaptacion: cap,
        returnTo: pipelineReturnTo,
      },
    });
  };

  const eliminarBorradorEntradaDirecta = async (cap) => {
    if (!cap?.id || eliminandoBorrador) return;
    const ok = await dialogs.confirm(
      `¿Eliminar el borrador «${etiquetaBorradorCaptacion(cap)}»? No se podrá recuperar.`
    );
    if (!ok) return;
    setEliminandoBorrador(true);
    try {
      await api.delete(`/captaciones/${cap.id}`, {
        params: { confirmar_borrador: "1" },
      });
      const restantes = borradoresEntradaDirecta.filter((b) => b.id !== cap.id);
      setBorradoresEntradaDirecta(restantes);
      setBorradorSeleccionadoId(null);
      if (restantes.length === 0) setMostrarBorradoresCaptacion(false);
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || "No se pudo eliminar el borrador");
    } finally {
      setEliminandoBorrador(false);
    }
  };

  const empezarNuevaCaptacionEnBlanco = () => {
    setMostrarBorradoresCaptacion(false);
    setBorradorSeleccionadoId(null);
    // No reutilizar el borrador anterior de sessionStorage (mezclaba datos).
    try {
      sessionStorage.removeItem("vitalcasa_ficha_captacion_id");
    } catch {
      /* ignore */
    }
    navigate("/propiedades/nueva", {
      state: {
        pendingNuevaCaptacion: true,
        entradaDirectaNueva: true,
        returnTo: pipelineReturnTo,
      },
    });
  };

  /** Datos visibles para elegir entre borradores (Ref manual, dirección, precio…). */
  const datosEtiquetaBorradorCaptacion = (cap) => {
    const ex = typeof cap?.datos_extras === "object" && cap.datos_extras ? cap.datos_extras : {};
    const ref = String(
      cap?.propiedad_ref
      || ex.referencia
      || ex.propiedad_ref
      || ex.ref
      || ex.referencia_propiedad
      || "",
    ).trim();
    const via = [ex.nombre_via || cap.calle, cap.numero || ex.numero].filter(Boolean).join(" ").trim();
    const pobRaw = cap.poblacion || ex.poblacion_nombre || ex.poblacion || "";
    const pob = /^\d{5}$/.test(String(pobRaw).trim()) ? "" : String(pobRaw).trim();
    const nom = [cap.nombre, cap.apellido].filter(Boolean).join(" ").trim();
    const tipoRaw = String(ex.tipo_inmueble_nombre || ex.tipo || cap.tipo_inmueble || "").trim();
    const tipo = /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(tipoRaw) ? "" : tipoRaw;
    const operacion = String(ex.operacion || cap.operacion || "").trim();
    let precio = cap.precio_venta ?? ex.precio_venta ?? null;
    if (precio != null && precio !== "") {
      const n = Number(precio);
      precio = Number.isFinite(n)
        ? `${n.toLocaleString("es-ES", { maximumFractionDigits: 0 })} €`
        : String(precio);
    } else {
      precio = "";
    }
    const lugar = [via || nom || "", pob].filter(Boolean).join(" · ") || "Sin dirección";
    const detalle = [tipo, operacion, precio].filter(Boolean).join(" · ");
    return { ref, lugar, detalle };
  };

  const etiquetaBorradorCaptacion = (cap) => {
    const { ref, lugar, detalle } = datosEtiquetaBorradorCaptacion(cap);
    const partes = [];
    if (ref) partes.push(`Ref. ${ref}`);
    partes.push(lugar);
    if (detalle) partes.push(detalle);
    return partes.join(" · ") || `Borrador ${String(cap.id || "").slice(0, 8)}`;
  };

  const cargarBorradoresEntradaDirecta = async () => {
    if (modo !== "captaciones") {
      setBorradoresEntradaDirecta([]);
      return;
    }
    try {
      const res = await api.get("/captaciones?borradores_entrada_directa=true");
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      setBorradoresEntradaDirecta(rows);
    } catch {
      setBorradoresEntradaDirecta([]);
    }
  };

  const cargar = async () => {
    cargarAbortRef.current?.abort();
    const ctrl = new AbortController();
    cargarAbortRef.current = ctrl;
    const mismoListado = endpointCargadoRef.current === endpointBase;
    const hayTablero = modo === "captaciones" && mismoListado && itemsRef.current.length > 0;
    if (!hayTablero) setCargando(true);
    try {
      const res = await api.get(endpointBase, { signal: ctrl.signal });
      setItems(res.data.data);
      endpointCargadoRef.current = endpointBase;
      if (modo === "captaciones") {
        void cargarBorradoresEntradaDirecta();
      } else {
        setBorradoresEntradaDirecta([]);
      }
      // Conteos Fríos/Eliminados: solo COUNT(*) (no bajar el listado entero).
      // El listado completo se carga al abrir el modal (cargarFrios / cargarEliminados).
      api.get(`${endpointBase}?frios=true&solo_conteo=true`, { signal: ctrl.signal })
        .then((r) => setConteoFrios(totalDesdeRespuestaLista(r)))
        .catch(() => {});
      api.get(`${endpointBase}?eliminados=true&solo_conteo=true`, { signal: ctrl.signal })
        .then((r) => setConteoEliminados(totalDesdeRespuestaLista(r)))
        .catch(() => {});
    } catch (err) {
      if (isAbortError(err)) return;
      console.error("Error cargando:", err);
    } finally {
      if (!ctrl.signal.aborted) setCargando(false);
    }
  };

  const abrirLeadParaEdicion = async (item) => {
    if (!item?.id) return;
    setLeadEditando(item);
    try {
      const res = await api.get(`/leads/${item.id}`);
      const data = res.data?.data;
      if (data?.id) {
        setLeadEditando((prev) => (prev?.id === item.id ? data : prev));
        setItems((prev) => prev.map((it) => (it.id === data.id ? { ...it, ...data } : it)));
      }
    } catch {
      /* ya abierto con datos de lista */
    }
  };

  // Clic en tarjeta del Pipeline (embudo):
  // - Con cliente vinculado → ficha del cliente
  // - Sin cliente → modal del lead
  const abrirLeadOFicha = async (item) => {
    if (!item?.id) return;
    let clienteId = item.cliente_id || null;
    if (!clienteId) {
      try {
        const res = await api.get(`/leads/${item.id}`);
        clienteId = res.data?.data?.cliente_id || null;
      } catch { /* sin ficha → modal del lead */ }
    }
    if (clienteId) {
      navigate(`/clientes/${clienteId}`, {
        state: {
          returnTo: pipelineReturnTo,
          leadId: item.id,
          demandaId: item.demanda_id || null,
        },
      });
      return;
    }
    await abrirLeadParaEdicion(item);
  };

  /**
   * Fríos: si el lead ya tiene cliente → ficha cliente (← Fríos);
   * si no se creó cliente → modal del lead.
   */
  const abrirLeadDesdeFrios = async (item) => {
    if (!item?.id) return;
    let clienteId = item.cliente_id || null;
    let leadFresh = null;
    if (!clienteId) {
      try {
        const res = await api.get(`/leads/${item.id}`);
        leadFresh = res.data?.data || null;
        clienteId = leadFresh?.cliente_id || null;
      } catch { /* sin ficha → modal lead */ }
    }
    if (clienteId) {
      const next = new URLSearchParams(location.search);
      next.set("modo", "leads");
      next.set("openFrios", "1");
      const q = next.toString();
      const leadRef = leadFresh || item;
      navigate(`/clientes/${clienteId}`, {
        state: {
          returnTo: `${location.pathname}${q ? `?${q}` : ""}`,
          leadId: leadRef.id,
          demandaId: leadRef.demanda_id || null,
        },
      });
      return;
    }
    await abrirLeadParaEdicion(leadFresh || item);
  };

  const abrirVistaPreviaRef = async (e, ref) => {
    e.stopPropagation();
    e.preventDefault();
    const referencia = String(ref || "").trim();
    if (!referencia) return;
    const propId = await resolverPropiedadIdPorReferencia(referencia);
    if (propId) {
      setVistaPreviaPropId(propId);
      return;
    }
    await dialogs.alert(`No se encontró la propiedad ${referencia}`);
  };

  const cargarEliminados = async () => {
    try {
      const res = await api.get(`${endpointBase}?eliminados=true`, { signal: pageAbortRef.current?.signal });
      setEliminados(res.data.data || []);
      setConteoEliminados(res.data.data?.length ?? 0);
    } catch (e) {
      if (isAbortError(e)) return;
      console.error(e);
    }
  };

  const cargarFrios = async () => {
    try {
      const res = await api.get(`${endpointBase}?frios=true`, { signal: pageAbortRef.current?.signal });
      setFrios(res.data.data || []);
      setConteoFrios(res.data.data?.length ?? 0);
    } catch (e) {
      if (isAbortError(e)) return;
      console.error(e);
    }
  };

  const ARCHIVAR_LEADS_TIMEOUT_MS = 120000;

  const archivarLeads = async (ids) => {
    const uniq = [...new Set((ids || []).map(String).filter(Boolean))];
    if (!uniq.length) return { ok: true, procesados: 0, fallos: [] };
    const { data } = await api.post(
      "/leads/mover-terminal-lote",
      {
        ids: uniq,
        destino: "eliminados",
        motivo_codigo: "archivo_pipeline",
        motivo_libre: "Archivado desde pipeline",
        afectar_cliente: false,
      },
      { timeout: ARCHIVAR_LEADS_TIMEOUT_MS }
    );
    return data;
  };

  const eliminarItems = async (ids) => {
    if (modo === "leads") {
      let result;
      try {
        result = await archivarLeads(ids);
      } catch (e) {
        console.error(e);
        const msg =
          e.response?.data?.error ||
          e.response?.data?.mensaje ||
          "No se pudo archivar el lead (Ventas y etapas terminales requieren mover-terminal).";
        await dialogs.alert(msg);
        return;
      }
      const falloIds = new Set((result?.fallos || []).map((f) => String(f.id)));
      const okIds = ids.filter((id) => !falloIds.has(String(id)));
      if (okIds.length) {
        setItems((prev) => prev.filter((l) => !okIds.includes(l.id)));
      }
      await Promise.all([cargar(), cargarEliminados()]);
      if (result?.fallos?.length) {
        await dialogs.alert(
          `Se archivaron ${okIds.length} de ${ids.length} lead(s). ${result.fallos.length} no se pudieron archivar.`
        );
      }
      return;
    }
    const etapaEliminar = "eliminado";
    for (const id of ids) {
      try {
        const item =
          items.find((i) => String(i.id) === String(id))
          || frios.find((i) => String(i.id) === String(id))
          || eliminados.find((i) => String(i.id) === String(id));
        const etapaFront = item ? getFrontendEtapa(item.etapa) : "";
        if (
          etapaFront === "para_revisar"
          && !puede("pl_capt_revisar_eliminar", "ver")
        ) {
          await dialogs.alert(
            "En «Para Revisar» no puedes eliminar. Solo secretaría, director o administrador.",
          );
          return;
        }
        await api.patch(`${endpointBase}/${id}/etapa`, { etapa: etapaEliminar });
      } catch (e) {
        console.error(e);
        await dialogs.alert(e.response?.data?.error || "No se pudo eliminar la captacion");
        return;
      }
    }
    setItems((prev) => prev.filter((l) => !ids.includes(l.id)));
    await cargar();
  };

  /** Archiva si hace falta y borra fisicamente (pruebas / limpieza desde cualquier columna). */
  const eliminarLeadsPermanente = async (ids) => {
    const uniq = [...new Set((ids || []).map(String).filter(Boolean))];
    if (!uniq.length) return;

    const needArchive = uniq.filter((id) => {
      const item =
        items.find((i) => String(i.id) === id) ||
        frios.find((i) => String(i.id) === id) ||
        eliminados.find((i) => String(i.id) === id);
      const etapa = String(item?.etapa || "").toLowerCase();
      return !(item?.activo === false || etapa === "eliminados" || etapa === "eliminado");
    });

    if (needArchive.length) {
      try {
        const archiveResult = await archivarLeads(needArchive);
        if (archiveResult?.fallos?.length) {
          await dialogs.alert(
            `No se pudieron archivar ${archiveResult.fallos.length} lead(s) antes del borrado definitivo.`
          );
          return;
        }
      } catch (e) {
        console.error(e);
        await dialogs.alert(
          e.response?.data?.error || e.response?.data?.mensaje || "No se pudieron archivar los leads antes del borrado"
        );
        return;
      }
    }

    const deleteResults = await Promise.allSettled(
      uniq.map((id) => api.delete(`/leads/${id}/permanente`))
    );
    const errCount = deleteResults.filter((r) => r.status === "rejected").length;
    if (errCount > 0) {
      await dialogs.alert(
        `No se pudieron borrar ${errCount} lead(s). Archívalos primero o revisa permisos.`
      );
    }
    await Promise.all([
      cargar(),
      mostrarEliminados ? cargarEliminados() : Promise.resolve(),
      mostrarFrios ? cargarFrios() : Promise.resolve(),
    ]);
  };

  const eliminarCaptacionesPermanente = async (ids) => {
    const uniq = [...new Set((ids || []).map(String).filter(Boolean))];
    if (!uniq.length) return;

    const deleteResults = await Promise.allSettled(
      uniq.map((id) => api.delete(`/captaciones/${id}/definitiva`))
    );
    const errCount = deleteResults.filter((r) => r.status === "rejected").length;
    if (errCount > 0) {
      const firstErr = deleteResults.find((r) => r.status === "rejected");
      const msg =
        firstErr?.reason?.response?.data?.error ||
        `No se pudieron borrar ${errCount} captación(es).`;
      await dialogs.alert(msg);
    }
    setEliminados((prev) => prev.filter((l) => !uniq.includes(String(l.id))));
    setConteoEliminados((prev) => {
      const n = typeof prev === "number" ? prev : uniq.length;
      return Math.max(0, n - (uniq.length - errCount));
    });
    await Promise.all([
      cargar(),
      mostrarEliminados ? cargarEliminados() : Promise.resolve(),
    ]);
  };

  const leadColumnEtapa = (it) =>
    modo === "leads" ? (leadDisplayEtapaOverride[it.id] ?? it.etapa) : getFrontendEtapa(it.etapa);

  // Set de agente_id correspondientes a los usuarios seleccionados en el filtro global.
  // Los items guardan agente_id (no usuario_id); cada usuario CRM puede vincularse vía usuarios.agente_id.
  // Captaciones de Ana: quien_trabaja='ana' y agente_id null → no entran por agente_id.
  const usuariosSeleccionadosFiltro = usuariosPipelineFiltro.length === 0
    ? []
    : usuariosPipelineLista.filter((u) =>
      usuariosPipelineFiltro.some((id) => String(id) === String(u.id))
    );
  const agenteIdsFiltroUsuarios = usuariosPipelineFiltro.length === 0
    ? null
    : new Set(
      usuariosSeleccionadosFiltro
        .filter((u) => u.agente_id)
        .map((u) => String(u.agente_id)),
    );
  const filtroIncluyeAna = usuariosSeleccionadosFiltro.some(esUsuarioAnaEnFiltroPipeline);

  const itemPasaFiltrosPipeline = useCallback((l) => {
    if (usuariosPipelineFiltro.length > 0) {
      const porAgente = l.agente_id != null
        && agenteIdsFiltroUsuarios
        && agenteIdsFiltroUsuarios.has(String(l.agente_id));
      // Misma regla que la tarjeta: Ana = quien_trabaja ana sin compañera, o agente_nombre Ana.
      const porAna = modo === "captaciones"
        && filtroIncluyeAna
        && agenteResponsableLabel(l) === "Ana";
      if (!porAgente && !porAna) return false;
    }
    if (filtroFechaPipeline !== "todos") {
      const ref = fechaReferenciaPipeline(l);
      const d = ref ? new Date(ref) : null;
      if (!filtroFechaPipelineMatch(filtroFechaPipeline, d, new Date(), fechaDesdePipeline, fechaHastaPipeline)) {
        return false;
      }
    }
    if (!itemCoincideBusquedaPipeline(l, filtroTextoPipeline)) return false;
    return true;
  }, [
    usuariosPipelineFiltro.length,
    agenteIdsFiltroUsuarios,
    filtroIncluyeAna,
    modo,
    filtroFechaPipeline,
    fechaDesdePipeline,
    fechaHastaPipeline,
    filtroTextoPipeline,
  ]);

  const hayFiltroFechaPipeline = filtroFechaPipeline !== "todos";
  const hayFiltroUsuariosPipeline = usuariosPipelineFiltro.length > 0;
  const hayFiltroTextoPipeline = filtroTextoPipeline.trim().length > 0;
  const numFiltrosPipelineActivos =
    (hayFiltroFechaPipeline ? 1 : 0)
    + (hayFiltroUsuariosPipeline ? 1 : 0)
    + (hayFiltroTextoPipeline ? 1 : 0);

  const limpiarFiltrosPipeline = useCallback(() => {
    setUsuariosPipelineFiltro([]);
    setFiltroFechaPipeline("todos");
    setFechaDesdePipeline("");
    setFechaHastaPipeline("");
    setFiltroTextoPipeline("");
  }, []);

  const itemsPorEtapaBase = (etapaKey) => {
    if (modo !== "captaciones") return items.filter((l) => leadColumnEtapa(l) === etapaKey);
    return items.filter((l) => {
      // Las propiedades (origen='propiedad') siempre van a la columna Captaciones.
      // Evita que propiedades con etapa_pipeline residual (p. ej. revisar_cap) inflen
      // Para Revisar. Las captaciones en etapas intermedias llegan como origen='captacion'
      // vía Branch 1 del API y se colocan con getFrontendEtapa.
      if (l.origen === "propiedad") return etapaKey === "captaciones";
      return getFrontendEtapa(l.etapa) === etapaKey;
    });
  };

  const itemsPorEtapa = (etapaKey) => itemsPorEtapaBase(etapaKey).filter(itemPasaFiltrosPipeline);

  /** Mismos filtros del modal aplicados sobre el array plano (embudo). */
  const itemsVisiblesPipeline = items.filter(itemPasaFiltrosPipeline);

  const itemVisibleInPipelineColumn = useCallback((item, etapaKey) => {
    if (modo !== "leads") return true;
    if (leadSkyPending.has(item.id) && etapaKey === "leads") return false;
    if (leadArcHide && leadArcHide.id === item.id && leadArcHide.from === etapaKey) return false;
    return true;
  }, [modo, leadSkyPending, leadArcHide]);

  const itemsPorEtapaVisible = (etapaKey) =>
    itemsPorEtapa(etapaKey).filter((it) => itemVisibleInPipelineColumn(it, etapaKey));

  const conteos = {};
  etapas.forEach(e => { conteos[e.key] = itemsPorEtapaVisible(e.key).length; });

  const etapaCaptacionesFinal = ETAPAS_CAPTACIONES.find((e) => e.key === "captaciones");
  const etapaVentasFinal = ETAPAS_LEADS.find((e) => e.key === "ventas");
  // Nodo de permisos de cada etapa/columna del Kanban: pl_leads_<key> / pl_capt_<key>
  // (coincide exactamente con los ids del árbol de permisos).
  const stageNodeId = (key) => (modo === "leads" ? `pl_leads_${key}` : `pl_capt_${key}`);
  const etapasColumnasVisibles = etapas.filter((e) => {
    if (!puede(stageNodeId(e.key), "ver")) return false;
    if (modo === "captaciones" && !captacionesColExpandida && e.key === "captaciones") return false;
    if (modo === "leads" && !ventasColExpandida && e.key === "ventas") return false;
    return true;
  });

  const onDragStart = (event, item) => {
    if (modo === "captaciones" && item.origen === "propiedad") return;
    try {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", item.id || "");
    } catch {}
    setDragging(item);
  };
  const onDragOver  = (e, etapaKey) => {
    e.preventDefault();
    if (!puede(stageNodeId(etapaKey), "modificar")) return;
    setDragOver(etapaKey);
  };
  const onDragLeave = () => setDragOver(null);
  const onDrop      = async (etapaKey) => {
    setDragOver(null);
    if (!dragging || getFrontendEtapa(dragging.etapa) === etapaKey) { setDragging(null); return; }

    // Sin permiso de "modificar" sobre la etapa destino, no se puede arrastrar hacia ella
    // (p. ej. un rol con Para Revisar en solo lectura no puede mover tarjetas ahí).
    if (!puede(stageNodeId(etapaKey), "modificar")) {
      setDragging(null);
      return;
    }

    // Desde «Para Revisar» el agente no mueve (ni a eliminados vía drop): solo secretaría/director/admin.
    if (
      modo === "captaciones"
      && getFrontendEtapa(dragging.etapa) === "para_revisar"
      && !puede("pl_capt_para_revisar", "modificar")
    ) {
      setDragging(null);
      await dialogs.alert(
        "En «Para Revisar» no puedes mover ni archivar la captación. Solo secretaría, director o administrador.",
      );
      return;
    }

    // Las propiedades no pueden moverse entre columnas del Pipeline de captaciones
    if (modo === "captaciones" && dragging.origen === "propiedad") {
      setDragging(null);
      return;
    }

    // Barrera para_revisar: verificar que campos del agente estén completos (todos los roles)
    if (etapaKey === "para_revisar" && modo === "captaciones" && dragging.origen !== "propiedad") {
      try {
        const verif = await api.get(`/campos-obligatorios/verificar-captacion/${dragging.id}?rol=agente`);
        if (!verif.data.puedeAvanzar) {
          setCaptacionCompletando(dragging);
          setDragging(null);
          return;
        }
      } catch (e) {
        console.error("[Pipeline] Error verificando campos agente:", e);
      }
    }

    // Transiciones que requieren datos: mostrar modal antes de cambiar etapa
    if (modo === "captaciones" && dragging.origen !== "propiedad") {
      const etapaOrigenFront = getFrontendEtapa(dragging.etapa);
      if (etapaOrigenFront === "precaptaciones" && etapaKey === "contactados") {
        setTransicionPendiente({ item: dragging, etapaDestino: etapaKey, tipo: "contacto" });
        setDragging(null);
        return;
      }
      if (etapaOrigenFront === "contactados" && etapaKey === "visita_concertada") {
        setTransicionPendiente({ item: dragging, etapaDestino: etapaKey, tipo: "cita" });
        setDragging(null);
        return;
      }
    }

    const fromKey = getFrontendEtapa(dragging.etapa);
    const useLeadArc =
      modo === "leads" &&
      fromKey !== etapaKey &&
      leadMotionRef.current;

    if (useLeadArc) {
      const item = dragging;
      setDragging(null);
      const apiPromise = patchEtapaApi(item, etapaKey, null);
      try {
        await leadMotionRef.current.enqueueArc({
          item,
          fromKey,
          toKey: etapaKey,
          onBeforeStart: () => setLeadArcHide({ id: item.id, from: fromKey }),
          onLand: async () => {
            try {
              await apiPromise;
              applyEtapaLocal(item, etapaKey);
            } catch (err) {
              console.error(err);
              const data = err?.response?.data;
              if (data?.bloqueado) {
                setCaptacionCompletando(item);
              } else {
                const msg = data?.error || err?.message || "No se pudo mover la tarjeta";
                void dialogs.alert(`Error moviendo tarjeta: ${msg}`);
              }
            } finally {
              setLeadArcHide(null);
            }
          },
        });
      } catch (e) {
        console.error(e);
        setLeadArcHide(null);
        try {
          await apiPromise;
        } catch (err) {
          const data = err?.response?.data;
          if (data?.bloqueado) setCaptacionCompletando(item);
        }
      }
      return;
    }

    await ejecutarCambioEtapa(dragging, etapaKey, null);
    setDragging(null);
  };

  const patchEtapaApi = async (item, etapaKey, historial_data) => {
    const etapaBackend = getBackendEtapa(etapaKey);
    const esPropiedad = modo === "captaciones" && item.origen === "propiedad";
    if (esPropiedad) {
      await api.put(`/propiedades/${item.id}`, { etapa_pipeline: etapaBackend });
    } else {
      await api.patch(`${endpointBase}/${item.id}/etapa`, { etapa: etapaBackend, historial_data });
    }
  };

  const applyEtapaLocal = (item, etapaKey) => {
    const etapaBackend = getBackendEtapa(etapaKey);
    setItems((prev) =>
      prev.map((l) =>
        l.id === item.id && l.origen === item.origen ? { ...l, etapa: etapaBackend } : l
      )
    );
    if (modo === "leads") {
      prevLeadRealEtapaRef.current.set(item.id, etapaBackend);
    }
  };

  // Ejecuta el cambio de etapa en el backend (reutilizado por onDrop y confirmarTransicion)
  const ejecutarCambioEtapa = async (item, etapaKey, historial_data) => {
    try {
      await patchEtapaApi(item, etapaKey, historial_data);
      applyEtapaLocal(item, etapaKey);
    } catch (err) {
      console.error(err);
      const data = err?.response?.data;
      if (data?.bloqueado) {
        setCaptacionCompletando(item);
      } else {
        const msg = data?.error || err?.message || "No se pudo mover la tarjeta";
        await dialogs.alert(`Error moviendo tarjeta: ${msg}`);
      }
    }
  };

  // Confirmar transición desde el modal (guarda historial y cambia etapa)
  const confirmarTransicion = async (historial_data) => {
    if (!transicionPendiente) return;
    const { item, etapaDestino } = transicionPendiente;
    setTransicionPendiente(null);

    // Caso especial: el agente eligió "Mandar a Eliminados" desde el modal
    if (historial_data?.resultado === 'eliminado') {
      try {
        await api.patch(`${endpointBase}/${item.id}/etapa`, {
          etapa: 'eliminado',
          historial_data,
        });
        await cargar();
      } catch (e) { console.error('[Pipeline] Error eliminando desde modal:', e); }
      return;
    }

    if (historial_data?.resultado === 'cita' && historial_data?.fecha_accion) {
      await completarTareaActivaCaptacion(api, item.id);
    }

    await ejecutarCambioEtapa(item, etapaDestino, historial_data);

    if (historial_data?.resultado === 'cita' && historial_data?.fecha_accion) {
      try {
        await crearCitaCaptacionAgenda(api, {
          captacion: item,
          historial_data,
          agenteId: item?.agente_id || usuario?.agente_id,
        });
      } catch (e) {
        console.error('[Pipeline] Error creando cita en agenda:', e);
        await dialogs.alert('La etapa se guardó, pero no se pudo crear la cita en la agenda. Créala manualmente desde Agenda.');
      }
    }
  };

  const abrirPrecaptacionCaptacion = async (item) => {
    setCamposFaltantesAgente([]);
    // Abrir al instante (el fetch no debe “tragarse” el clic).
    setCaptacionEditando(item);
    try {
      const res = await api.get(`/captaciones/${item.id}`);
      const data = res.data?.data || res.data;
      if (data?.id) {
        setCaptacionEditando((prev) => (prev?.id === item.id ? { ...item, ...data } : prev));
      }
    } catch (e) {
      console.warn("[Pipeline] refrescar precaptación:", e?.message || e);
    }
  };

  /** Eliminadas / frías: abrir ficha según etapa previa al archivo (sin cerrar la tabla). */
  const abrirCaptacionDesdeListado = async (item) => {
    let cap = item;
    try {
      const res = await api.get(`/captaciones/${item.id}`);
      const data = res.data?.data || res.data;
      if (data?.id) cap = { ...item, ...data };
    } catch (e) {
      console.warn("[Pipeline] refrescar captación archivada:", e?.message || e);
    }
    const etapaKey = etapaFrontendCaptacionArchivada(cap);
    if (cap?.origen === "propiedad") {
      navigate(`/propiedades/${cap.id}/editar`, { state: { returnTo: pipelineReturnTo } });
      return;
    }
    if (etapaKey === "captaciones" && cap.propiedad_id) {
      navigate(`/propiedades/${cap.propiedad_id}/editar`, { state: { returnTo: pipelineReturnTo } });
      return;
    }
    if (etapaKey === "para_revisar") {
      setCaptacionRevisando(cap);
      return;
    }
    if (etapaKey === "visita_concertada") {
      void abrirModalVisitaConcertada(cap);
      return;
    }
    void abrirPrecaptacionCaptacion(cap);
  };

  // Clic en tarjeta de captación
  const abrirModalVisitaConcertada = async (item) => {
    // Abrir al instante con datos de lista; refrescar después.
    if (shouldOpenCompletarCamposAgente(item)) {
      // El modal ya hace GET + verificar-captacion. No refrescar aquí:
      // antes un 2º setCaptacion reiniciaba el modal (doble spinner).
      setCaptacionCompletando(item);
      return;
    }
    setCamposFaltantesAgente([]);
    setCaptacionEditando(item);
    let cap = item;
    try {
      const res = await api.get(`/captaciones/${item.id}`);
      const data = res.data?.data || res.data;
      if (data?.id) {
        cap = {
          ...item,
          ...data,
          fecha_cita: data.fecha_cita ?? item.fecha_cita,
          fecha_fin_cita: data.fecha_fin_cita ?? item.fecha_fin_cita,
        };
      }
    } catch (e) {
      console.warn("[Pipeline] refrescar captación visita_concertada:", e?.message || e);
    }

    if (shouldOpenCompletarCamposAgente(cap)) {
      setCaptacionCompletando(cap);
      setCaptacionEditando((prev) => (prev?.id === item.id ? null : prev));
      return;
    }
    setCamposFaltantesAgente([]);
    setCaptacionCompletando((prev) => (prev?.id === item.id ? null : prev));
    setCaptacionEditando((prev) => (prev?.id === item.id || prev?.id === cap.id ? cap : prev));
  };

  const handleCardClick = (item, etapaKey) => {
    // Si es propiedad, abrir su ficha y no tratarla como captación
    if (item?.origen === "propiedad") {
      navigate(`/propiedades/${item.id}/editar`, { state: { returnTo: pipelineReturnTo } });
      return;
    }
    if (etapaKey === "captaciones") {
      if (item.propiedad_id) {
        navigate(`/propiedades/${item.propiedad_id}/editar`, { state: { returnTo: pipelineReturnTo } });
      }
      return;
    }
    if (etapaKey === "para_revisar") {
      const bloqueo = multimediaPendienteRevision(item);
      if (bloqueo?.duro) {
        void dialogs.alert(bloqueo.mensaje);
        return;
      }
      if (bloqueo) {
        void (async () => {
          if (await dialogs.confirm(bloqueo.mensaje)) setCaptacionRevisando(item);
        })();
        return;
      }
      setCaptacionRevisando(item);
      return;
    }
    // Visita Concertada → modal de visita (no ficha completa)
    if (etapaKey === "visita_concertada" && item.origen !== "propiedad") {
      void abrirModalVisitaConcertada(item);
      return;
    }
    // Precaptaciones / Contactados (incl. las enviadas desde MarketPlace)
    if (etapaKey === "precaptaciones" || etapaKey === "contactados") {
      void abrirPrecaptacionCaptacion(item);
    }
  };

  const restaurar = async (itemId, lista, setLista) => {
    try {
      await api.patch(`${endpointBase}/${itemId}/restaurar`);
      await cargar();
      setLista(prev => prev.filter(l => l.id !== itemId));
    } catch (err) { console.error(err); }
  };

  const enviarAFrios = async (ids, setLista) => {
    const idList = [...ids];
    if (!idList.length) return;
    try {
      await Promise.all(idList.map((id) => api.patch(`${endpointBase}/${id}/enviar-a-frios`)));
      setLista((prev) => prev.filter((l) => !idList.includes(l.id)));
      setConteoEliminados((prev) => {
        const n = typeof prev === "number" ? prev : idList.length;
        return Math.max(0, n - idList.length);
      });
      void cargar();
      void cargarFrios();
    } catch (err) {
      console.error(err);
      await dialogs.alert("No se pudo enviar a Fríos. Inténtalo de nuevo.");
      void cargarEliminados();
    }
  };

  const enviarAActivos = async (ids, setLista) => {
    const idList = [...ids];
    if (!idList.length) return;
    try {
      await Promise.all(idList.map((id) => api.patch(`${endpointBase}/${id}/enviar-a-activos`)));
      setLista((prev) => prev.filter((l) => !idList.includes(l.id)));
      setConteoEliminados((prev) => {
        const n = typeof prev === "number" ? prev : idList.length;
        return Math.max(0, n - idList.length);
      });
      void cargar();
    } catch (err) {
      console.error(err);
      await dialogs.alert("No se pudo enviar a Activos. Inténtalo de nuevo.");
      void cargarEliminados();
    }
  };

  const procesarEmail = async () => {
    if (!textoEmail.trim()) return;
    setProcesandoEmail(true);
    setResultadoEmail(null);
    try {
      const res = await api.post("/ia/procesar-email", { texto_email: textoEmail, modo });
      setResultadoEmail(res.data);
      if (res.data.ok && res.data.es_lead && !res.data.lead_existente) await cargar();
    } catch (err) {
      const raw =
        err.response?.data?.error ||
        err.response?.data?.message ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.message ||
        "Error al procesar email";
      // Asegura SIEMPRE string: si el backend devuelve un objeto (p. ej. {message})
      // renderizarlo directamente rompe React (#31 "objects are not valid as a child").
      const detail =
        typeof raw === "string"
          ? raw
          : raw?.message || raw?.error || (() => { try { return JSON.stringify(raw); } catch { return "Error al procesar email"; } })();
      setResultadoEmail({ error: String(detail) });
    } finally {
      setProcesandoEmail(false);
    }
  };

  useEffect(() => {
    if (!mostrarEmail || !resultadoEmail) return;
    if (resultadoEmail.error || !resultadoEmail.es_lead || resultadoEmail.lead_existente) return;
    const id = window.setTimeout(() => {
      setMostrarEmail(false);
      setTextoEmail("");
      setResultadoEmail(null);
    }, 2000);
    return () => window.clearTimeout(id);
  }, [mostrarEmail, resultadoEmail]);

  const btnPrimary = {
    padding: "8px 18px", borderRadius: 8, border: "none",
    background: c.accentBlue, color: "#fff", fontSize: 13, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
  };
  const btnSecondary = {
    padding: "8px 18px", borderRadius: 8,
    border: `1px solid ${c.border}`, background: "transparent",
    color: c.textSecondary, fontSize: 13, fontWeight: 500,
    cursor: "pointer", fontFamily: "inherit",
  };
  const inputStyle = {
    width: "100%", padding: "8px 12px", fontSize: 13,
    border: `1px solid ${c.border}`, borderRadius: 8,
    background: c.bgInput, color: c.textPrimary, outline: "none",
    fontFamily: "inherit", transition: "border-color 0.15s",
  };

  return (
    <div style={{ height: "calc(100vh - 49px)", display: "flex", overflow: "hidden", background: c.bgApp }}>

      {/* ═══ PANEL IZQUIERDO ═══ */}
      <div style={{ width: 220, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 10px", borderRight: `1px solid ${c.border}`, background: c.bgPanel, gap: 12 }}>

        {/* Toggle Leads / Captaciones */}
        <div style={{ display: "flex", width: "100%", borderRadius: 10, overflow: "hidden", border: `1px solid ${c.border}`, background: c.bgCard }}>
          {["leads", "captaciones"].map(m => (
            <button key={m} onClick={() => setModo(m)}
              style={{
                flex: 1, padding: "7px 4px", fontSize: 11, fontWeight: 700,
                border: "none", cursor: "pointer", fontFamily: "inherit",
                textTransform: "capitalize", letterSpacing: "0.02em", transition: "all 0.2s",
                background: modo === m
                  ? (m === "leads" ? "linear-gradient(135deg, #3b82f6, #2563eb)" : "linear-gradient(135deg, #10b981, #059669)")
                  : "transparent",
                color: modo === m ? "#fff" : c.textMuted,
              }}>
              {m === "leads" ? "Leads" : "Captaciones"}
            </button>
          ))}
        </div>

        <EmbudoSVG conteos={conteos} etapas={etapas} modo={modo} items={itemsVisiblesPipeline} theme={theme} getFrontendEtapa={getFrontendEtapa}
          onClickItem={(item) => {
            if (modo === "leads") abrirLeadOFicha(item);
            else handleCardClick(item, getFrontendEtapa(item.etapa));
          }}
          onOpenLead={(item) => abrirLeadOFicha(item)}
          onEliminarItems={eliminarItems}
          onEliminarPermanente={modo === "leads" ? eliminarLeadsPermanente : undefined}
        />

        <div style={{ width: "80%", height: 1, background: c.border }} />

        {/* ── Botón Frías/Fríos ── */}
        {puede(modo === "leads" ? "pl_leads_frios" : "pl_capt_frios", "ver") && (
        <button
          onClick={() => { setMostrarFrios(true); cargarFrios(); }}
          style={{
            width: "100%", padding: "9px 12px", borderRadius: 10, cursor: "pointer",
            fontFamily: "inherit", fontSize: 14, fontWeight: 700, letterSpacing: "0.04em",
            border: "1px solid rgba(96,165,250,0.35)",
            background: isDark
              ? "linear-gradient(135deg,rgba(37,99,235,0.22),rgba(59,130,246,0.12))"
              : "linear-gradient(135deg,rgba(219,234,254,0.9),rgba(191,219,254,0.6))",
            color: "#3b82f6",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            transition: "all 0.18s",
            boxShadow: "0 1px 6px rgba(59,130,246,0.12)",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(59,130,246,0.28)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(96,165,250,0.35)"; e.currentTarget.style.boxShadow = "0 1px 6px rgba(59,130,246,0.12)"; }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07" />
            </svg>
            {modo === "captaciones" ? "Frías" : "Fríos"}
          </div>
          {conteoFrios !== null && conteoFrios > 0 && (
            <span style={{
              background: "rgba(59,130,246,0.18)", color: "#60a5fa",
              borderRadius: 20, padding: "1px 8px", fontSize: 13, fontWeight: 700,
            }}>{conteoFrios}</span>
          )}
        </button>
        )}

        {/* ── Botón Eliminadas/Eliminados ── */}
        {puede(modo === "leads" ? "pl_leads_eliminados" : "pl_capt_eliminados", "ver") && (
        <button
          onClick={() => { setMostrarEliminados(true); cargarEliminados(); }}
          style={{
            width: "100%", padding: "9px 12px", borderRadius: 10, cursor: "pointer",
            fontFamily: "inherit", fontSize: 14, fontWeight: 700, letterSpacing: "0.04em",
            border: "1px solid rgba(239,68,68,0.35)",
            background: isDark
              ? "linear-gradient(135deg,rgba(185,28,28,0.22),rgba(239,68,68,0.12))"
              : "linear-gradient(135deg,rgba(254,226,226,0.9),rgba(254,202,202,0.6))",
            color: "#ef4444",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            transition: "all 0.18s",
            boxShadow: "0 1px 6px rgba(239,68,68,0.12)",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#ef4444"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(239,68,68,0.28)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(239,68,68,0.35)"; e.currentTarget.style.boxShadow = "0 1px 6px rgba(239,68,68,0.12)"; }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            </svg>
            {modo === "captaciones" ? "Eliminadas" : "Eliminados"}
          </div>
          {conteoEliminados !== null && conteoEliminados > 0 && (
            <span style={{
              background: "rgba(239,68,68,0.18)", color: "#f87171",
              borderRadius: 20, padding: "1px 8px", fontSize: 13, fontWeight: 700,
            }}>{conteoEliminados}</span>
          )}
        </button>
        )}

        {/* ── Botón Filtros (fechas + usuarios) ── */}
        <div style={{ width: "100%" }}>
          <button
            type="button"
            onClick={() => {
              setMostrarModalFiltrosPipeline(true);
              if (puedeFiltrarPorUsuariosPipeline) cargarUsuariosPipeline();
            }}
            style={{
              width: "100%", padding: "9px 12px", borderRadius: 10, cursor: "pointer",
              fontFamily: "inherit", fontSize: 14, fontWeight: 700, letterSpacing: "0.04em",
              border: "1px solid rgba(139,92,246,0.35)",
              background: isDark
                ? "linear-gradient(135deg,rgba(109,40,217,0.22),rgba(139,92,246,0.12))"
                : "linear-gradient(135deg,rgba(237,233,254,0.9),rgba(221,214,254,0.6))",
              color: "#7c3aed",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              transition: "all 0.18s",
              boxShadow: "0 1px 6px rgba(139,92,246,0.12)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(139,92,246,0.28)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(139,92,246,0.35)"; e.currentTarget.style.boxShadow = "0 1px 6px rgba(139,92,246,0.12)"; }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filtros
            </div>
            {numFiltrosPipelineActivos > 0 && (
              <span style={{
                background: "rgba(139,92,246,0.18)", color: "#a78bfa",
                borderRadius: 20, padding: "1px 8px", fontSize: 13, fontWeight: 700,
              }}>{numFiltrosPipelineActivos}</span>
            )}
          </button>
        </div>

        {mostrarModalFiltrosPipeline && createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Filtros del pipeline"
            style={{
              position: "fixed", inset: 0, zIndex: 1200,
              background: "rgba(0,0,0,0.55)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 16,
            }}
            onClick={() => setMostrarModalFiltrosPipeline(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%", maxWidth: 560,
                maxHeight: "min(94vh, 920px)",
                overflow: "auto",
                background: c.bgCard,
                border: `1px solid ${c.border}`,
                borderRadius: 14,
                boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
                padding: "18px 20px 16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: c.textPrimary }}>Filtros</h3>
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: c.textMuted }}>
                    Aplican a todas las columnas del pipeline ({modo === "leads" ? "Leads" : "Captaciones"})
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setMostrarModalFiltrosPipeline(false)}
                  style={{ background: "none", border: "none", fontSize: 20, color: c.textMuted, cursor: "pointer", padding: "2px 6px", lineHeight: 1 }}
                  aria-label="Cerrar"
                >✕</button>
              </div>

              {/* Búsqueda libre */}
              <div style={{ marginBottom: 18 }}>
                <p style={{
                  margin: "0 0 10px", fontSize: 11, fontWeight: 700, color: c.textMuted,
                  textTransform: "uppercase", letterSpacing: "0.06em",
                }}>Búsqueda</p>
                <input
                  type="search"
                  value={filtroTextoPipeline}
                  onChange={(e) => setFiltroTextoPipeline(e.target.value)}
                  placeholder="Nombre, teléfono, ref, dirección, procedencia, notas…"
                  aria-label="Buscar en tarjetas del pipeline"
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "10px 12px", borderRadius: 9,
                    border: `1px solid ${c.border}`,
                    background: isDark ? c.bgPanel : "#fff",
                    color: c.textPrimary,
                    fontFamily: "inherit", fontSize: 14,
                  }}
                />
              </div>

              {/* Fecha */}
              <div style={{ marginBottom: 18 }}>
                <p style={{
                  margin: "0 0 10px", fontSize: 11, fontWeight: 700, color: c.textMuted,
                  textTransform: "uppercase", letterSpacing: "0.06em",
                }}>Fecha</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {[
                    { id: "todos", label: "Todas" },
                    { id: "hoy", label: "Hoy" },
                    { id: "esta_semana", label: "Esta semana" },
                    { id: "este_mes", label: "Este mes" },
                    { id: "este_anio", label: "Este año" },
                    { id: "custom", label: "Personalizada" },
                  ].map((opt) => {
                    const activo = filtroFechaPipeline === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => {
                          setFiltroFechaPipeline(opt.id);
                          if (opt.id !== "custom") {
                            setFechaDesdePipeline("");
                            setFechaHastaPipeline("");
                          } else {
                            setTimeout(() => abrirPickerFecha(fechaDesdePipelineRef.current), 50);
                          }
                        }}
                        style={{
                          padding: "7px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit",
                          fontSize: 12, fontWeight: 700,
                          border: `1px solid ${activo ? "#7c3aed" : c.border}`,
                          background: activo
                            ? (isDark ? "rgba(124,58,237,0.28)" : "rgba(124,58,237,0.12)")
                            : (isDark ? "rgba(255,255,255,0.04)" : "#f8fafc"),
                          color: activo ? (isDark ? "#c4b5fd" : "#6d28d9") : c.textSecondary,
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {filtroFechaPipeline === "custom" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: c.textMuted, fontWeight: 600 }}>
                      Desde
                      <input
                        ref={fechaDesdePipelineRef}
                        type="date"
                        value={fechaDesdePipeline}
                        onChange={(e) => setFechaDesdePipeline(e.target.value)}
                        onClick={(e) => abrirPickerFecha(e.currentTarget)}
                        style={{
                          padding: "7px 10px", borderRadius: 8, border: `1px solid ${c.border}`,
                          background: isDark ? c.bgPanel : "#fff", color: c.textPrimary,
                          fontFamily: "inherit", fontSize: 13, cursor: "pointer",
                          colorScheme: isDark ? "dark" : "light", minWidth: 148,
                        }}
                      />
                    </label>
                    <span style={{ color: c.textMuted, marginTop: 16 }}>—</span>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: c.textMuted, fontWeight: 600 }}>
                      Hasta
                      <input
                        type="date"
                        value={fechaHastaPipeline}
                        onChange={(e) => setFechaHastaPipeline(e.target.value)}
                        onClick={(e) => abrirPickerFecha(e.currentTarget)}
                        style={{
                          padding: "7px 10px", borderRadius: 8, border: `1px solid ${c.border}`,
                          background: isDark ? c.bgPanel : "#fff", color: c.textPrimary,
                          fontFamily: "inherit", fontSize: 13, cursor: "pointer",
                          colorScheme: isDark ? "dark" : "light", minWidth: 148,
                        }}
                      />
                    </label>
                  </div>
                )}
              </div>

              {/* Usuarios */}
              {puedeFiltrarPorUsuariosPipeline && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <p style={{
                      margin: 0, fontSize: 11, fontWeight: 700, color: c.textMuted,
                      textTransform: "uppercase", letterSpacing: "0.06em",
                    }}>Usuarios</p>
                    {usuariosPipelineFiltro.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setUsuariosPipelineFiltro([])}
                        style={{ fontSize: 11, color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}
                      >
                        Limpiar usuarios
                      </button>
                    )}
                  </div>
                  {cargandoUsuariosPipeline && usuariosPipelineLista.length === 0 ? (
                    <p style={{ fontSize: 12, color: c.textMuted, margin: 0 }}>Cargando usuarios…</p>
                  ) : usuariosPipelineLista.length === 0 ? (
                    <div>
                      <p style={{ fontSize: 12, color: c.textMuted, margin: "0 0 8px" }}>
                        {errorUsuariosPipeline || "No hay usuarios."}
                      </p>
                      <button
                        type="button"
                        onClick={() => cargarUsuariosPipeline()}
                        style={{
                          fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer",
                          padding: "6px 10px", borderRadius: 8,
                          border: `1px solid ${c.border}`,
                          background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                          color: "#7c3aed",
                        }}
                      >
                        Reintentar
                      </button>
                    </div>
                  ) : (
                    <div style={{
                      border: `1px solid ${c.border}`, borderRadius: 10,
                      padding: "4px 6px",
                      background: isDark ? "rgba(0,0,0,0.15)" : "#f8fafc",
                    }}>
                      <label style={{
                        display: "flex", alignItems: "center", gap: 8, padding: "8px 6px",
                        borderRadius: 6, cursor: "pointer",
                        borderBottom: `1px solid ${c.border}`,
                      }}>
                        <input
                          type="checkbox"
                          checked={usuariosPipelineFiltro.length === 0}
                          onChange={() => setUsuariosPipelineFiltro([])}
                          style={{ cursor: "pointer" }}
                        />
                        <span style={{ fontSize: 13, color: c.textSecondary, fontWeight: 700, fontStyle: "italic" }}>(Todos)</span>
                      </label>
                      {usuariosPipelineLista.map((u) => {
                        const checked = usuariosPipelineFiltro.some((id) => String(id) === String(u.id));
                        return (
                          <label key={u.id} style={{
                            display: "flex", alignItems: "center", gap: 8, padding: "7px 6px",
                            borderRadius: 6, cursor: "pointer",
                          }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                setUsuariosPipelineFiltro((prev) =>
                                  checked
                                    ? prev.filter((id) => String(id) !== String(u.id))
                                    : [...prev, String(u.id)]
                                );
                              }}
                              style={{ cursor: "pointer" }}
                            />
                            <span style={{ fontSize: 13, color: c.textSecondary }}>{u.nombre}</span>
                            <span style={{
                              fontSize: 10, color: c.textMuted, marginLeft: "auto",
                              textTransform: "capitalize",
                            }}>{u.rol}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div style={{
                display: "flex", gap: 8, marginTop: 18, paddingTop: 14,
                borderTop: `1px solid ${c.border}`,
              }}>
                <button
                  type="button"
                  onClick={limpiarFiltrosPipeline}
                  disabled={numFiltrosPipelineActivos === 0}
                  style={{
                    flex: 1, padding: "10px 12px", borderRadius: 9, cursor: numFiltrosPipelineActivos ? "pointer" : "default",
                    fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                    border: `1px solid ${c.border}`,
                    background: "transparent",
                    color: numFiltrosPipelineActivos ? "#ef4444" : c.textMuted,
                    opacity: numFiltrosPipelineActivos ? 1 : 0.55,
                  }}
                >
                  Limpiar todo
                </button>
                <button
                  type="button"
                  onClick={() => setMostrarModalFiltrosPipeline(false)}
                  style={{
                    flex: 1.4, padding: "10px 12px", borderRadius: 9, cursor: "pointer",
                    fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                    border: "none",
                    background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                    color: "#fff",
                  }}
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Botones acción */}
        {puedeCrearPipeline && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6, marginTop: "auto" }}>
            {puedeProcesarEmail && (
              <button onClick={() => { setResultadoEmail(null); setMostrarEmail(true); }} style={{ ...btnPrimary, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "linear-gradient(135deg, #10b981, #059669)" }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" /></svg>
                Procesar email
              </button>
            )}
            {puedeNuevoRegistro && (
              <button onClick={() => setMostrarForm(true)} style={{ ...btnPrimary, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                {modo === "leads" ? "Nuevo lead" : "Nueva precaptación"}
              </button>
            )}
            {modo === "captaciones" && puedeNuevoRegistro && (
              <>
                <button
                  onClick={handleNuevaCaptacion}
                  style={{ ...btnPrimary, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "linear-gradient(135deg, #8b5cf6, #7c3aed)" }}
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Nueva captación
                </button>
                {borradoresEntradaDirecta.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setBorradorSeleccionadoId(null);
                      setMostrarBorradoresCaptacion(true);
                    }}
                    style={{
                      ...btnPrimary,
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      background: "transparent",
                      color: "#7c3aed",
                      border: "1px solid #c4b5fd",
                      boxShadow: "none",
                    }}
                  >
                    Borradores ({borradoresEntradaDirecta.length})
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ═══ PANEL DERECHO — COLUMNAS ═══ */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {modo === "captaciones" && !captacionesColExpandida && etapaCaptacionesFinal && (
          <button
            type="button"
            onClick={() => toggleCaptacionesColExpandida(true)}
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "9px 14px",
              border: "none",
              borderBottom: `2px solid ${etapaCaptacionesFinal.color}`,
              background: isDark ? `${etapaCaptacionesFinal.color}14` : `${etapaCaptacionesFinal.color}22`,
              cursor: "pointer",
              fontFamily: "inherit",
              width: "100%",
              textAlign: "left",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                {etapaCaptacionesFinal.label}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <span style={{
                fontSize: 14, fontWeight: 800, color: "#fff",
                background: etapaCaptacionesFinal.gradient,
                padding: "2px 10px", borderRadius: 20, minWidth: 24, textAlign: "center",
              }}>
                {conteos.captaciones ?? 0}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: etapaCaptacionesFinal.color }}>
                Mostrar ▾
              </span>
            </div>
          </button>
        )}

        {modo === "leads" && !ventasColExpandida && etapaVentasFinal && (
          <button
            type="button"
            onClick={() => toggleVentasColExpandida(true)}
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "9px 14px",
              border: "none",
              borderBottom: `2px solid ${etapaVentasFinal.color}`,
              background: isDark ? `${etapaVentasFinal.color}14` : `${etapaVentasFinal.color}22`,
              cursor: "pointer",
              fontFamily: "inherit",
              width: "100%",
              textAlign: "left",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                {etapaVentasFinal.label}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <span style={{
                fontSize: 14, fontWeight: 800, color: "#fff",
                background: etapaVentasFinal.gradient,
                padding: "2px 10px", borderRadius: 20, minWidth: 24, textAlign: "center",
              }}>
                {conteos.ventas ?? 0}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: etapaVentasFinal.color }}>
                Mostrar ▾
              </span>
            </div>
          </button>
        )}

      <div
        style={{
          flex: 1,
          display: "flex",
          gap: 0,
          minWidth: 0,
          width: "100%",
          overflowX: modo === "captaciones" ? "auto" : "hidden",
          overflowY: "hidden",
        }}>
        {cargando ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: c.textMuted, fontSize: 13 }}>Cargando...</span>
          </div>
        ) : (
          etapasColumnasVisibles.map((etapa, i) => {
            const itemsEtapa = itemsPorEtapa(etapa.key);
            const itemsMostrar = itemsPorEtapaVisible(etapa.key);
            const isDragTarget = dragOver === etapa.key;
            const recibiendoAna = modo === "leads" && leadReceivingCol === etapa.key;
            return (
              <div key={etapa.key}
                onDragOver={(e) => onDragOver(e, etapa.key)}
                onDragLeave={onDragLeave}
                onDrop={() => onDrop(etapa.key)}
                style={{
                  flex: modo === "captaciones" ? "1 1 0" : 1,
                  minWidth: modo === "captaciones" ? 220 : 0,
                  maxWidth: modo === "captaciones" ? "none" : undefined,
                  width: modo === "captaciones" ? 0 : "auto",
                  flexShrink: modo === "captaciones" ? 1 : 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  borderRight: i < etapasColumnasVisibles.length - 1
                    ? (isDark ? `1px solid ${c.border}` : `2px solid rgba(30, 41, 59, 0.22)`)
                    : "none",
                  background: isDragTarget ? (isDark ? `${etapa.color}10` : `${etapa.color}14`) : "transparent",
                  transition: "background 0.2s",
                  boxShadow: recibiendoAna ? `inset 0 0 0 1px ${etapa.color}55, inset 0 0 28px ${etapa.color}18` : "none",
                }}>
                {/* Header columna */}
                <div
                  ref={(el) => {
                    if (modo === "leads") {
                      if (el) colHeaderRefs.current[etapa.key] = el;
                      else delete colHeaderRefs.current[etapa.key];
                    }
                  }}
                  style={{ padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `2px solid ${etapa.color}`, background: isDark ? `${etapa.color}12` : `${etapa.color}18` }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, textTransform: "uppercase", letterSpacing: "0.03em" }}>{etapa.label}</span>
                    {modo === "captaciones" && etapa.key === "captaciones" && (
                      <button
                        type="button"
                        title="Ocultar columna Captaciones"
                        onClick={(e) => { e.stopPropagation(); toggleCaptacionesColExpandida(false); }}
                        style={{
                          padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 700,
                          border: `1px solid ${etapa.color}55`, background: `${etapa.color}18`,
                          color: etapa.color, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                        }}
                      >
                        Ocultar ▴
                      </button>
                    )}
                    {modo === "leads" && etapa.key === "ventas" && (
                      <button
                        type="button"
                        title="Ocultar columna Ventas"
                        onClick={(e) => { e.stopPropagation(); toggleVentasColExpandida(false); }}
                        style={{
                          padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 700,
                          border: `1px solid ${etapa.color}55`, background: `${etapa.color}18`,
                          color: etapa.color, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                        }}
                      >
                        Ocultar ▴
                      </button>
                    )}
                    {recibiendoAna && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: etapa.color, background: `${etapa.color}22`, border: `1px solid ${etapa.color}44`, padding: "2px 6px", borderRadius: 6, whiteSpace: "nowrap" }}>
                        Ana atendiendo
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 800, color: "#fff", background: etapa.gradient, padding: "2px 10px", borderRadius: 20, minWidth: 24, textAlign: "center" }}>{conteos[etapa.key]}</span>
                </div>
                {/* Lista */}
                <div
                  ref={(el) => {
                    if (modo === "leads") {
                      if (el) colBodyRefs.current[etapa.key] = el;
                      else delete colBodyRefs.current[etapa.key];
                    }
                  }}
                  style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 8, display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {itemsEtapa.length === 0 ? (
                    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 0" }}>
                      <span style={{ fontSize: 11, color: c.textPlaceholder, fontStyle: "italic" }}>Arrastra aquí</span>
                    </div>
                  ) : itemsMostrar.length === 0 ? (
                    <div style={{ flex: 1, minHeight: 48 }} />
                  ) : (
                    itemsMostrar.map(item => (
                      <div key={item.id} className="pipeline-card-in-column" style={{ flexShrink: 0, width: "100%", display: "flex", justifyContent: "center" }}>
                        <div style={{ width: `${PIPELINE_CARD_WIDTH_RATIO * 100}%`, maxWidth: "100%", minWidth: 0 }}>
                          {modo === "leads" ? (
                            <TarjetaLead
                              ref={(el) => {
                                if (el) leadCardRefs.current[item.id] = el;
                                else delete leadCardRefs.current[item.id];
                              }}
                              item={item}
                              etapa={etapa}
                              theme={theme}
                              onClick={() => abrirLeadOFicha(item)}
                              onDragStart={(e) => onDragStart(e, item)}
                              onAbrirVistaPreviaRef={abrirVistaPreviaRef}
                            />
                          ) : (
                            <TarjetaCaptacion item={item} etapa={etapa} theme={theme}
                              onClick={() => handleCardClick(item, etapa.key)}
                              onDragStart={(e) => onDragStart(e, item)}
                              canDrag={item.origen !== "propiedad"} />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      </div>

      {modo === "leads" && !cargando && (
        <PipelineLeadMotionOverlay
          ref={leadMotionRef}
          theme={theme}
          etapasLeads={ETAPAS_LEADS}
          speedMultiplier={ANA_PIPELINE_MOTION_SPEED}
          LeadCard={TarjetaLead}
          colHeaderRefs={colHeaderRefs}
          colBodyRefs={colBodyRefs}
          leadCardRefs={leadCardRefs}
          onReceivingColumn={setLeadReceivingCol}
        />
      )}

      {/* ═══ MODAL — PROCESAR EMAIL ═══ */}
      {mostrarEmail && (
        <div style={{ position: "fixed", inset: 0, background: c.overlayBg, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => { setMostrarEmail(false); setTextoEmail(""); setResultadoEmail(null); }}>
          <div style={{ background: c.bgPanel, borderRadius: 16, padding: 24, width: "100%", maxWidth: 640, boxShadow: "0 25px 50px rgba(0,0,0,0.25)", border: `1px solid ${c.border}`, maxHeight: "90vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: c.textPrimary, margin: 0 }}>Procesar email</h2>
                <p style={{ fontSize: 12, color: c.textMuted, margin: "4px 0 0" }}>Pega el contenido del email y la IA extraerá los datos automáticamente</p>
              </div>
              <button onClick={() => { setMostrarEmail(false); setTextoEmail(""); setResultadoEmail(null); }} style={{ background: "none", border: "none", fontSize: 20, color: c.textMuted, cursor: "pointer", padding: 4 }}>✕</button>
            </div>
            <textarea value={textoEmail} onChange={e => setTextoEmail(e.target.value)} rows={8}
              placeholder="Pega aquí el contenido del email..."
              style={{ ...inputStyle, resize: "vertical", marginBottom: 12, lineHeight: 1.5 }}
              onFocus={e => e.target.style.borderColor = c.borderFocus}
              onBlur={e => e.target.style.borderColor = c.border} />
            {resultadoEmail && (
              <div style={{ marginBottom: 12, padding: 14, borderRadius: 10, fontSize: 13, lineHeight: 1.5,
                ...(resultadoEmail.error
                  ? { background: isDark ? "#2a1215" : "#fef2f2", border: `1px solid ${isDark ? "#5c1d1d" : "#fecaca"}`, color: c.accentRed }
                  : !resultadoEmail.es_lead
                  ? { background: isDark ? "#2a2010" : "#fffbeb", border: `1px solid ${isDark ? "#5c4a1d" : "#fde68a"}`, color: c.accentOrange }
                  : { background: isDark ? "#0f2a1a" : "#f0fdf4", border: `1px solid ${isDark ? "#14532d" : "#bbf7d0"}`, color: c.accentGreen }
                ) }}>
                {resultadoEmail.error ? <span>❌ {typeof resultadoEmail.error === "string" ? resultadoEmail.error : (resultadoEmail.error?.message || "Error al procesar email")}</span>
                  : !resultadoEmail.es_lead ? <span>⚠️ No es un lead: {typeof resultadoEmail.motivo === "string" ? resultadoEmail.motivo : ""}</span>
                  : (
                    <div>
                      <p style={{ fontWeight: 700, marginBottom: 6 }}>✅ Lead creado correctamente</p>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, fontSize: 12 }}>
                        {resultadoEmail.datos?.nombre    && <span><b style={{ color: c.textMuted }}>Nombre:</b> {resultadoEmail.datos.nombre}</span>}
                        {resultadoEmail.datos?.email     && <span><b style={{ color: c.textMuted }}>Email:</b> {resultadoEmail.datos.email}</span>}
                        {resultadoEmail.datos?.telefono  && <span><b style={{ color: c.textMuted }}>Teléfono:</b> {resultadoEmail.datos.telefono}</span>}
                        {resultadoEmail.datos?.operacion && <span><b style={{ color: c.textMuted }}>Operación:</b> {resultadoEmail.datos.operacion}</span>}
                        {resultadoEmail.lead?.idioma && <span><b style={{ color: c.textMuted }}>Idioma:</b> {String(resultadoEmail.lead.idioma).toUpperCase()}</span>}
                      </div>
                      {resultadoEmail.ana_contacto?.mode === "queued" && (
                        <p style={{ marginTop: 10, fontSize: 11, color: c.textSecondary, borderTop: `1px solid ${c.border}`, paddingTop: 8 }}>
                          ⏳ {resultadoEmail.ana_contacto.message || "Ana está iniciando el contacto (llamada y embudo) en segundo plano."}
                        </p>
                      )}
                      {["voice", "voice_test_call_started"].includes(resultadoEmail.ana_contacto?.mode) && (
                        <p style={{ marginTop: 10, fontSize: 11, color: c.textSecondary, borderTop: `1px solid ${c.border}`, paddingTop: 8 }}>
                          📞 <b>Llamada de Ana iniciada</b> (ElevenLabs).
                        </p>
                      )}
                      {resultadoEmail.ana_contacto?.mode === "whatsapp_only" && (
                        <p style={{ marginTop: 10, fontSize: 11, color: c.textSecondary, borderTop: `1px solid ${c.border}`, paddingTop: 8 }}>
                          💬 <b>Solo WhatsApp</b> (no llamada):{" "}
                          {resultadoEmail.ana_contacto.skippedVoiceReason === "ANA_VOICE_OUTREACH_ENABLED=false"
                            ? "la voz está desactivada (ANA_VOICE_OUTREACH_ENABLED=false)."
                            : resultadoEmail.ana_contacto.missingEnv?.length
                              ? `faltan variables en Railway: ${resultadoEmail.ana_contacto.missingEnv.join(", ")}.`
                              : "revisa credenciales ElevenLabs en el servidor."}
                        </p>
                      )}
                      {["voice_failed", "call_failed", "trigger_exception"].includes(resultadoEmail.ana_contacto?.mode) && (
                        <p style={{ marginTop: 10, fontSize: 11, color: c.accentOrange, borderTop: `1px solid ${c.border}`, paddingTop: 8 }}>
                          ⚠️ <b>No se pudo iniciar la llamada</b>: {typeof resultadoEmail.ana_contacto.error === "string" ? resultadoEmail.ana_contacto.error : (resultadoEmail.ana_contacto.error?.message || "error desconocido")}
                          {resultadoEmail.ana_contacto.sentWhatsAppFallback ? " Se ha enviado WhatsApp como respaldo." : ""}
                        </p>
                      )}
                      {String(resultadoEmail.ana_contacto?.mode || "").startsWith("skipped_") && (
                        <p style={{ marginTop: 10, fontSize: 11, color: c.textSecondary, borderTop: `1px solid ${c.border}`, paddingTop: 8 }}>
                          ℹ️ <b>Ana no llamó</b>: {resultadoEmail.ana_contacto.mode}
                        </p>
                      )}
                    </div>
                  )}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => { setMostrarEmail(false); setTextoEmail(""); setResultadoEmail(null); }} style={btnSecondary}>Cerrar</button>
              <button onClick={procesarEmail} disabled={procesandoEmail || !textoEmail.trim()}
                style={{ ...btnPrimary, background: "linear-gradient(135deg, #10b981, #059669)", opacity: (procesandoEmail || !textoEmail.trim()) ? 0.5 : 1 }}>
                {procesandoEmail ? "🤖 Analizando..." : "🤖 Procesar con IA"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL — FRÍOS ═══ */}
      {mostrarFrios && (
        <ModalTablaEmbudo
          etapa={{ label: modo === "leads" ? "Leads fríos" : "Captaciones frías", color: "#64748b", gradient: "linear-gradient(135deg, #94a3b8, #64748b)" }}
          items={frios}
          modo={modo}
          theme={theme}
          tipoModal="frios"
          cerrarAlAbrirItem={false}
          onAbrirVistaPreviaRef={modo === "leads" ? abrirVistaPreviaRef : undefined}
          onClose={() => setMostrarFrios(false)}
          crearCitaDesdeFrio={modo === "leads"}
          usuario={usuario}
          onCitaCreada={async () => {
            await cargarFrios();
            await cargar();
          }}
          onClickItem={(item) => {
            if (modo === "leads") void abrirLeadDesdeFrios(item);
            else void abrirCaptacionDesdeListado(item);
          }}
          onRestaurar={async (ids) => {
            for (const id of ids) await restaurar(id, frios, setFrios);
          }}
          onEliminar={async (ids) => {
            if (modo === "leads") {
              let result;
              try {
                result = await archivarLeads(ids);
              } catch (e) {
                console.error(e);
                await dialogs.alert(
                  e.response?.data?.error || e.response?.data?.mensaje || "No se pudo archivar el lead"
                );
                return;
              }
              const falloIds = new Set((result?.fallos || []).map((f) => String(f.id)));
              const okIds = ids.filter((id) => !falloIds.has(String(id)));
              if (okIds.length) {
                setFrios((prev) => prev.filter((l) => !okIds.includes(l.id)));
              }
              await Promise.all([cargarFrios(), cargarEliminados()]);
              if (result?.fallos?.length) {
                await dialogs.alert(
                  `Se archivaron ${okIds.length} de ${ids.length} lead(s). ${result.fallos.length} no se pudieron archivar.`
                );
              }
            } else {
              for (const id of ids) {
                try {
                  await api.patch(`${endpointBase}/${id}/etapa`, { etapa: "eliminado" });
                } catch (e) {
                  console.error(e);
                  await dialogs.alert(e.response?.data?.error || "No se pudo eliminar la captación");
                  return;
                }
              }
              setFrios((prev) => prev.filter((l) => !ids.includes(l.id)));
              await cargar();
            }
          }}
        />
      )}

      {/* ═══ MODAL — ELIMINADOS ═══ */}
      {mostrarEliminados && (
        <ModalTablaEmbudo
          etapa={{ label: modo === "leads" ? "Leads eliminados" : "Captaciones eliminadas", color: "#ef4444", gradient: "linear-gradient(135deg, #f87171, #ef4444)" }}
          items={eliminados}
          modo={modo}
          theme={theme}
          tipoModal="eliminados"
          cerrarAlAbrirItem={false}
          onAbrirVistaPreviaRef={modo === "leads" ? abrirVistaPreviaRef : undefined}
          onClose={() => setMostrarEliminados(false)}
          onClickItem={(item) => {
            if (modo === "leads") abrirLeadOFicha(item);
            else void abrirCaptacionDesdeListado(item);
          }}
          onRestaurar={async (ids) => {
            if (modo === "leads") {
              await enviarAFrios(ids, setEliminados);
            } else {
              for (const id of ids) await restaurar(id, eliminados, setEliminados);
            }
          }}
          restaurarLabel={modo === "leads" ? "Enviar a Fríos" : "↩ Restaurar seleccionados"}
          onEnviarAActivos={
            modo === "leads"
              ? async (ids) => {
                  await enviarAActivos(ids, setEliminados);
                }
              : undefined
          }
          onEliminarPermanente={
            modo === "leads" ? eliminarLeadsPermanente : eliminarCaptacionesPermanente
          }
        />
      )}

      {/* ═══ MODAL — COMPLETAR CAMPOS AGENTE (Visita Concertada) ═══
          No cerrar CompletarCampos al abrir ficha/modal original: al cerrarlos se vuelve aquí. */}
      <ModalCompletarCamposAgente
        isOpen={captacionCompletando !== null}
        captacion={captacionCompletando}
        onClose={() => setCaptacionCompletando(null)}
        onAvanzado={async () => { await cargar(); setCaptacionCompletando(null); }}
        onOpenFicha={(cap) => { setFichaOverlayCaptacion(cap); }}
        onOpenModalOriginal={(cap) => {
          setCamposFaltantesAgente([]);
          setCaptacionEditando(cap);
        }}
      />

      {/* ═══ MODAL — REVISAR CAPTACIÓN ═══ */}
      <ModalRevisarCaptacion
        isOpen={captacionRevisando !== null}
        captacion={captacionRevisando}
        onClose={() => setCaptacionRevisando(null)}
        onValidado={async (opts) => {
          if (opts?.retrocedido) {
            const cap = captacionRevisando;
            setCaptacionRevisando(null);
            await cargar();
            if (cap) {
              setRetrocesoMotivoPendiente({
                captacion: { ...cap, etapa: 'visita_cap' },
                motivoRetroceso: opts.motivoRetroceso,
                mensajeRetroceso: opts.mensaje,
                camposFaltantes: opts.camposFaltantes || [],
              });
            }
            return;
          }
          await cargar();
          if (opts?.finalizado) setCaptacionRevisando(null);
        }}
        onOpenFichaOverlay={(cap) => setFichaOverlayCaptacion(cap)}
        onOpenFicha={(cap) => { setCaptacionRevisando(null); navigate('/propiedades/nueva', { state: { fromCaptacion: cap, returnTo: pipelineReturnTo } }); }}
      />

      <ModalMotivoRetrocesoCaptacion
        isOpen={retrocesoMotivoPendiente !== null}
        captacion={retrocesoMotivoPendiente?.captacion}
        motivoRetroceso={retrocesoMotivoPendiente?.motivoRetroceso}
        camposFaltantes={retrocesoMotivoPendiente?.camposFaltantes}
        onClose={() => setRetrocesoMotivoPendiente(null)}
        onConfirm={async () => {
          setRetrocesoMotivoPendiente(null);
          await cargar();
        }}
      />

      {fichaOverlayCaptacion && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1080,
            background: "rgba(0,0,0,0.78)",
            display: "flex",
            flexDirection: "column",
            fontFamily: "'DM Sans', sans-serif",
          }}
          onClick={() => { void fichaOverlayCloseRef.current?.(); }}
        >
          <div
            style={{ flexShrink: 0, display: "flex", justifyContent: "flex-end", padding: "10px 12px", gap: 8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => { void fichaOverlayCloseRef.current?.(); }}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.35)",
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Cerrar ficha
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
            <PropiedadFormOverlay
              fromCaptacion={fichaOverlayCaptacion}
              returnTo={pipelineReturnTo}
              onClose={() => setFichaOverlayCaptacion(null)}
              onReadyClose={(fn) => { fichaOverlayCloseRef.current = fn; }}
            />
          </div>
        </div>
      )}

      {/* ═══ MODAL — NUEVO REGISTRO ═══ */}
      {modo === "leads" ? (
        <ModalNuevoLead
          isOpen={mostrarForm || leadEditando !== null}
          lead={leadEditando}
          onClose={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("openLead");
            if (next.toString() !== searchParams.toString()) {
              setSearchParams(next, { replace: true });
            }
            setMostrarForm(false);
            setLeadEditando(null);
            cargar();
            if (mostrarFrios) cargarFrios();
            if (mostrarEliminados) cargarEliminados();
          }}
          onCreated={() => { cargar(); setLeadEditando(null); }}
          onLeadUpdated={(actualizado) => {
            // Si el modal está cerrado (prev === null) NO lo reabrimos: un refresco en
            // vuelo del panel (p. ej. al vencer la tarea) resolvía tras cerrar y reabría
            // el modal solo a los ~2 s. Solo actualizamos el lead abierto si sigue abierto.
            setLeadEditando((prev) => (prev ? { ...prev, ...actualizado } : null));
            if (actualizado?.id) {
              setItems((prev) => prev.map((it) => (it.id === actualizado.id ? { ...it, ...actualizado } : it)));
            }
          }}
        />
      ) : (
        <ModalNuevaPrecaptacion
          isOpen={mostrarForm || captacionEditando !== null}
          captacion={captacionEditando}
          camposFaltantes={camposFaltantesAgente}
          onClose={() => { setMostrarForm(false); setCaptacionEditando(null); setCamposFaltantesAgente([]); }}
          onCreated={(autoAvanzado) => { cargar(); setCaptacionEditando(null); setCamposFaltantesAgente([]); }}
          onRemoteUpdate={setCaptacionEditando}
        />
      )}

      {/* Modal borradores «Nueva captación» (entrada_directa sin validar) */}
      {mostrarBorradoresCaptacion && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1200,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
          onClick={() => {
            setMostrarBorradoresCaptacion(false);
            setBorradorSeleccionadoId(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: "28px 32px",
              maxWidth: 480,
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            }}
          >
            <h2 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: "#1e1b4b" }}>
              Borradores de Nueva captación
            </h2>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b", lineHeight: 1.45 }}>
              Selecciona un borrador para continuar o eliminarlo. Verás la Ref. si la pusiste a mano,
              más dirección / propietario, tipo y precio. No aparecen en Visita ni en Para Revisar.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
              {borradoresEntradaDirecta.map((cap) => {
                const seleccionado = borradorSeleccionadoId === cap.id;
                const { ref, lugar, detalle } = datosEtiquetaBorradorCaptacion(cap);
                return (
                  <button
                    key={cap.id}
                    type="button"
                    onClick={() => setBorradorSeleccionadoId(cap.id)}
                    style={{
                      textAlign: "left",
                      padding: "12px 14px",
                      borderRadius: 10,
                      border: seleccionado ? "2px solid #7c3aed" : "1px solid #e2e8f0",
                      background: seleccionado ? "#f5f3ff" : "#f8fafc",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#1e293b",
                      boxShadow: seleccionado ? "0 0 0 1px #c4b5fd" : "none",
                    }}
                  >
                    {ref ? (
                      <span style={{ display: "block", fontWeight: 800, color: "#5b21b6", marginBottom: 2 }}>
                        Ref. {ref}
                      </span>
                    ) : (
                      <span style={{ display: "block", fontWeight: 600, color: "#94a3b8", marginBottom: 2, fontSize: 12 }}>
                        Sin referencia
                      </span>
                    )}
                    <span style={{ display: "block" }}>{lugar}</span>
                    {detalle ? (
                      <span style={{ display: "block", marginTop: 2, fontSize: 12, fontWeight: 600, color: "#64748b" }}>
                        {detalle}
                      </span>
                    ) : null}
                    {cap.updated_at && (
                      <span style={{ display: "block", marginTop: 4, fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>
                        Actualizado {new Date(cap.updated_at).toLocaleString("es-ES", {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 20 }}>
              <button
                type="button"
                onClick={() => {
                  setMostrarBorradoresCaptacion(false);
                  setBorradorSeleccionadoId(null);
                }}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                  color: "#64748b",
                }}
              >
                Cerrar
              </button>
              <button
                type="button"
                disabled={!borradorSeleccionadoId || eliminandoBorrador}
                onClick={() => {
                  const cap = borradoresEntradaDirecta.find((b) => b.id === borradorSeleccionadoId);
                  if (cap) void eliminarBorradorEntradaDirecta(cap);
                }}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "1px solid #fecaca",
                  background: borradorSeleccionadoId && !eliminandoBorrador ? "#fff" : "#f8fafc",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: borradorSeleccionadoId && !eliminandoBorrador ? "pointer" : "not-allowed",
                  color: borradorSeleccionadoId && !eliminandoBorrador ? "#dc2626" : "#94a3b8",
                  opacity: borradorSeleccionadoId ? 1 : 0.55,
                }}
              >
                {eliminandoBorrador ? "Eliminando…" : "Eliminar"}
              </button>
              <button
                type="button"
                disabled={!borradorSeleccionadoId || eliminandoBorrador}
                onClick={() => {
                  const cap = borradoresEntradaDirecta.find((b) => b.id === borradorSeleccionadoId);
                  if (cap) continuarBorradorEntradaDirecta(cap);
                }}
                style={{
                  padding: "10px 18px",
                  borderRadius: 10,
                  border: "none",
                  background: borradorSeleccionadoId && !eliminandoBorrador
                    ? "linear-gradient(135deg, #8b5cf6, #7c3aed)"
                    : "#e2e8f0",
                  color: borradorSeleccionadoId && !eliminandoBorrador ? "#fff" : "#94a3b8",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: borradorSeleccionadoId && !eliminandoBorrador ? "pointer" : "not-allowed",
                }}
              >
                Continuar
              </button>
              <button
                type="button"
                disabled={eliminandoBorrador}
                onClick={empezarNuevaCaptacionEnBlanco}
                style={{
                  padding: "10px 18px",
                  borderRadius: 10,
                  border: "1px solid #c4b5fd",
                  background: "#fff",
                  color: "#7c3aed",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: eliminandoBorrador ? "not-allowed" : "pointer",
                }}
              >
                Empezar nueva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de transición entre etapas (drag con datos requeridos) */}
      <ModalTransicionEtapa
        isOpen={transicionPendiente !== null}
        tipo={transicionPendiente?.tipo}
        captacion={transicionPendiente?.item}
        onClose={() => setTransicionPendiente(null)}
        onConfirmar={confirmarTransicion}
      />

      {vistaPreviaPropId && (
        <FichaResumenPropiedad propiedadId={vistaPreviaPropId} onClose={() => setVistaPreviaPropId(null)} />
      )}
    </div>
  );
}
